import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  CloseOutlined,
  DownloadOutlined,
  MenuOutlined,
  SaveOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import {
  allSectionsOf,
  evaluateConditions,
  getInstanceCount,
  hasUniquenessGroup,
  isDisabledByRule,
  isStepEligibleForAudience,
  mirrorSourceOf,
  ownsValueKey,
  renderInstanceLabel,
  resolveActiveBranch,
  scopeValuesToInstance,
  sectionMinInstances,
  withheldFieldIds,
  type StepAudience,
  type TemplateOwnedIds,
} from '@bmo/form-core';
import { Alert, Button, message, Modal, Tooltip, Typography } from 'antd';
import JSZip from 'jszip';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApplicationSessionExpiredError, type ESignSubmission } from '@/api/applications';
import {
  type FormSection,
  isLayoutBlockType,
  stepNavTitle,
  type WorkflowField,
  type WorkflowStep,
  type WorkflowTemplate,
  workflowsApi,
} from '@/api/workflows';
import { notifySessionActivity } from '@/auth/sessionTimer';
import { applySessionRepCodes, type MeResponse, useSession } from '@/auth/useSession';

import { AdditionalDocumentsStep } from './AdditionalDocumentsStep';
import { advisorSourceKeyOf, decideAdvisorPrefill, requiredExemptionFor } from './advisorPrefill';
import { refreshAdvisorRepCodes, stepNeedsRepCodes } from './advisorRepCodeRefresh';
import { ApplicationReviewStep } from './ApplicationReviewStep';
import {
  advanceStatus,
  BMO_STATUS,
  ceilingForStep,
  demoteStatus,
  initialStatusFor,
  isAdvisorWritable,
  isLocked,
  lockedReasonKey,
  milestoneForStep,
  promoteFromInitiated,
} from './bmoStatus';
import { type ApplicationSnapshot, buildApplicationSnapshot } from './buildApplicationSnapshot';
import { buildESignSubmission } from './buildESignSubmission';
import { clearAnswersHiddenBy } from './clearHiddenAnswers';
import { ClientLanguageContext } from './clientLanguage';
import { columnCellViolation } from './columnCellRules';
import { resolvePrefillSourceInstance } from './conditionalPrefill';
import { DateFormatContext, resolveDateFormat } from './dateFormat';
import { applyDefaultOptions } from './defaultOptions';
import { collectDeliverableFiles, deliverAndConfirm, keysOf } from './deliverAdditionalDocuments';
import { isMaskIncomplete } from './displayMask';
import { EmailVerifyContext } from './emailVerifyContext';
import { type EmailVerifyLookup, emailValueVerdict } from './emailVerifyGate';
import { localizedEntryLabel } from './entryLabel';
import { ESignInfoProbe, isESignInfoProbeEnabled } from './ESignInfoProbe';
import { type FieldValue, type RepeatableRowValue, isBlockingValueMatch } from './FieldRenderer';
import {
  meetsMinSelections,
  runValidate,
  validateDateRules,
  type ValidateRule,
} from './fieldValidation';
import { FormRenderer } from './FormRenderer';
import { type FormShellMode, type RendererMode, rendererModeFor } from './formShellMode';
import { hiddenBorrowedFieldIds } from './hiddenReveals';
import { fieldLengthViolation } from './lengthRule';
import { syncMirroredInstanceCounts } from './mirroredSections';
import { fieldNumberRangeViolation } from './numberRange';
import { OneSpanESignStep } from './OneSpanESignStep';
import { findOptionRedirect, redirectMessageFor } from './optionRedirect';
import { type PdfCache, PdfGenerationStep } from './PdfGenerationStep';
import {
  applicablePdfDocs,
  buildFilledPdfBlobUrl,
  packageZipName,
  pdfFillValues,
  type PdfGenerator,
  triggerBlobDownload,
} from './pdfPreviewActions';
import { PlaceholderStep } from './PlaceholderStep';
import { primaryClientName } from './primaryClientName';
import { ProcessingModal } from './ProcessingModal';
import { pruneStaleChoices } from './pruneStaleChoices';
import {
  isSumSatisfied,
  readUnitMode,
  resolveColumnLabel,
  unbalancedUnitColumns,
} from './repeatableSum';
import { clearStepAnswers, resetStepFor, stepAnswerKeys } from './resetStepAnswers';
import { RestartFromBlockContext } from './restartContext';
import { deriveResumePosition } from './resumePosition';
import { ShowErrorsContext } from './showErrorsContext';
import { StepProgress } from './StepProgress';
import { SubmitErrorModal } from './SubmitErrorModal';
import { classifySubmitFailure } from './submitFailure';
import { ThankYouStep } from './ThankYouStep';
import {
  fieldVisibleForUniqueness,
  findAllUniquenessConflicts,
  findUniquenessConflict,
} from './uniqueness';
import type { UploadedFilesStore } from './uploadedFiles';
import {
  collectEntryMeta,
  flattenUploadedFiles,
  hasBlockedAdditionalDocuments,
  isScanBlocked,
} from './uploadedFiles';
import { useAdditionalDocumentScanStatus } from './useAdditionalDocumentScanStatus';
import {
  type AdditionalDocumentUploader,
  useAdditionalDocumentUpload,
} from './useAdditionalDocumentUpload';
import { useEmailVerification } from './useEmailVerification';
import { resolveValidationMessage } from './validationMessage';
import { decideVariablePrefill, isVariableSourcedField } from './variablePrefill';
import { WelcomeStep } from './WelcomeStep';

const { Text } = Typography;

/**
 * Resolves once the browser has actually painted. Two frames, not one: the first callback runs
 * before the paint of the frame it was queued in, so only the second is guaranteed to be on the
 * far side of pixels reaching the screen.
 *
 * Used to get a busy state on screen before the work that blocks the main thread begins.
 * `requestAnimationFrame` is undefined in a non-DOM test environment, so fall through to a
 * resolved promise there rather than hanging.
 */
const nextPaint = (): Promise<void> =>
  typeof requestAnimationFrame === 'function'
    ? new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      })
    : Promise.resolve();

/** A store with nothing uploaded. Shared by the initial state and the branch swap, which
 *  both mean "this form has no documents yet" — and frozen so neither can be mutated into
 *  the other's state. */
const EMPTY_UPLOADS: UploadedFilesStore = Object.freeze({ entries: {}, bucket: [] });

type Props = {
  template: WorkflowTemplate;
  language: 'en' | 'fr';
  mode: FormShellMode;
  /** Step to land on when the shell mounts. Falls back to the first eligible
   *  step (and a saved-draft step if a localStorage draft exists). Used by
   *  the Approver review path so clicking "Preview" on a specific step rail
   *  card opens the form on that step. */
  initialStepId?: string;
  /** Seed values for testing — Originator/Approver Preview can pass a fixture
   *  loaded from `public/sample-values.json` so the form lands pre-filled and
   *  the Application Review step has data to recap. In advisor mode these are
   *  the answers a resume read back from BMO. */
  initialValues?: Record<string, FieldValue>;
  /** True when `initialValues` came from resuming a saved application (SFA-8)
   *  rather than from a preview fixture. Opens the shell on the first step that
   *  still needs the advisor and unlocks everything behind it — see
   *  `resumePosition.ts` for why the position is derived rather than stored.
   *  Ignored when `initialStepId` is given: an explicit deep link wins. */
  isResumedApplication?: boolean;
  /** Told the primary client's name whenever it changes, for the page's "CLIENT:" badge — null
   *  while there is none to show yet. See `primaryClientName`. */
  onPrimaryClientNameChange?: (name: string | null) => void;
  /** Fired when the advisor clicks "Save and exit" — caller decides where to
   *  go (typically back to the workflow detail page). Optional so existing
   *  call sites that don't need it can stay as-is. */
  onSaveAndExit?: () => void;
  /** The Welcome step's CANCEL. Absent = the same exit as Save & exit. The advisor page sends it
   *  to BMO's SmartForms landing page (INFLOWS-486 #1). */
  onWelcomeCancel?: () => void;
  /** BMO's id for the in-progress application, returned by the first save call. Required to build
   *  an additional document's spec §14.1 delivery name, so additional-document uploads stay
   *  local-only until it is supplied. Never set in preview. */
  applicationId?: string;
  /**
   * Pushes the full application snapshot to BMO (ADR-0010 / A12 — whole state, never a delta).
   * Fires on Save and exit, on Next, and on the Welcome step's CONTINUE.
   *
   * Absent outside advisor runtime, and that absence is the guard: originator and approver
   * PREVIEW must never write an application to BMO, and with no handler there is no write path
   * to reach — the same "absence rather than a flag" reasoning as
   * {@link useAdditionalDocumentUpload}.
   *
   * Rejecting blocks the navigation that triggered it. Nothing is stored locally, so advancing
   * past a failed save is how an advisor's work goes missing.
   *
   * The second argument is the application's `bmoStatus` as of this snapshot — see
   * {@link bmoStatus} for the ladder that computes it. It travels WITH the values it describes
   * rather than in a call of its own, so BMO's status can never disagree with BMO's data.
   *
   * The third argument is the e-signature block — the signer roster and uploaded supporting
   * documents (see {@link buildESignSubmission}). Always present, carrying empty arrays until the
   * workflow's e-signature step has signers to resolve; empty is what keeps `bmoData` off early
   * saves, since the BFF omits the member rather than sending a half-built one.
   *
   * The fourth argument is the instant of the press that triggered this save, present only when
   * a delivery follows it on the same press ({@link persistAndDeliver}). It stamps every advisor
   * upload's §14.1 name, and the deliver call recomposes those same names from the same value —
   * so the handler must pass it through untouched rather than re-reading a clock. Absent on every
   * other save, where the server falls back to the application's creation instant.
   */
  onSave?: (
    snapshot: ApplicationSnapshot,
    bmoStatus: string,
    eSign: ESignSubmission,
    deliveryTimestamp?: string,
  ) => Promise<void>;
  /**
   * The advisor's final submit — the point-of-no-return call. Absent outside advisor runtime,
   * same "absence rather than a flag" guard as {@link onSave}: preview must never tell BMO an
   * application is submitted, and with no handler there is no call to reach.
   *
   * Deliberately NOT routed through {@link onSave}: submitting is a dedicated BMO call (the save
   * endpoint refuses SUBMITTED outright, BMO_STATUS_NOT_WRITABLE), and it carries one more thing
   * `onSave` does not need — every additional-document key this application's steps require,
   * across the whole template, so the server can re-check delivery as a backstop
   * (PROJECT_PLAN.md: "same gate re-applied at SUBMIT").
   *
   * Resolves with where DC FileX delivery landed. A failed delivery REJECTS as of 2026-10-23
   * (`502 PACKAGE_DELIVERY_FAILED`) rather than resolving `'pending'`: BMO does not act on an
   * application until its package arrives, so a submit whose package never landed is not a
   * finished submit. `'pending'` survives in the type only for a rolling deploy answering an old
   * API — see {@link ApplicationSubmitResult}. Calling this again is always safe.
   *
   * The fourth argument is what the delivered package's PDFs are FILLED from — the same map
   * every preview and download fills from ({@link pdfFillValues}). It travels separately from
   * `snapshot` because the two answer different questions: the snapshot is the labelled record
   * BMO stores, and it is an allowlist that silently omits any answer no FIELD holds. Filling
   * from it printed a dual-unit column's percentages into its dollar boxes. See
   * `pdfFillValues` for the whole of that argument.
   */
  onSubmit?: (
    snapshot: ApplicationSnapshot,
    eSign: ESignSubmission,
    additionalDocumentKeys: string[],
    pdfValues: Record<string, FieldValue>,
  ) => Promise<{ deliveryStatus: 'delivered' | 'pending' }>;
  /**
   * The status BMO last confirmed for this application — from the resume read, or echoed back
   * by the last save. Seeds the ladder at mount; after that the shell owns the rungs it authors
   * and only re-adopts this value when BMO has taken the application over (a locked status),
   * which read-onlys the shell on the spot.
   *
   * Absent in preview and before a resume has ever succeeded, in which case the shell starts at
   * IN-PROGRESS.
   */
  bmoStatus?: string;
  /**
   * BMO's creation timestamp for this application, echoed back on every save.
   *
   * Read by the dev-only eSignInfo probe and nothing else — it is what §14.1 package delivery
   * names are stamped with, so without it the names the probe displays carry a server-clock
   * timestamp the real save will not use. Absent in preview and before the first save returns one.
   */
  creationTimestamp?: string;
  /**
   * Renders the whole application inert: fields disabled, no Save / Next / Continue, no
   * uploads. OR'd with the status-derived lock, and reserved for the spec's `mode=r` query param
   * (docs/pretend-advisor.md swap-point #9) — the status lock needs no help from the caller.
   */
  readOnly?: boolean;
  /**
   * The advisor picked a RadioGroup answer the originator marked as belonging on a DIFFERENT
   * form, confirmed the modal, and is leaving. Exactly one of `templateId` / `url` is set.
   *
   * A callback rather than a navigation, for the same reason {@link onSaveAndExit} is one: this
   * file knows nothing about routes, and `templateId` means different destinations on different
   * surfaces — that workflow's PREVIEW for originator/approver, a fresh APPLICATION on it for
   * an advisor. The host page is the only thing that knows which it is.
   *
   * Absent = no redirect modal is ever shown, which is what keeps a caller that cannot navigate
   * from offering the advisor a button that does nothing.
   */
  onOptionRedirect?: (target: { templateId: string | null; url: string | null }) => void;
  /**
   * The answers have selected a different form for the rest of the workflow — or, with
   * null, have selected their way back to the base one.
   *
   * A callback rather than a load, for the same reason {@link onOptionRedirect} is one:
   * this file does not fetch templates, and the host owns which `template` prop it hands
   * down. The host answers by composing the branched template and passing it back in,
   * WITHOUT changing the shell's `key` — a remount here would discard every answer given,
   * including the one that triggered the swap.
   *
   * Every host that wires this does it through `useFormBranch`, which owns the fetch, the
   * cache and the composition. Supplying this prop by hand means reimplementing that
   * contract; prefer the hook.
   *
   * Absent = branching never fires. That is now a per-surface choice rather than a rule:
   * the advisor runtime, both previews and the public `/ext/` runtime all wire it. The
   * only thing a host needs is a way to load one workflow by id, which is why the hook
   * takes that as an argument instead of importing a client.
   */
  onFormBranchChange?: (targetTemplateId: string | null) => void;
  /**
   * Which branch the host has already composed into {@link template}, and which field ids
   * that branch owns. Null / absent = the base form.
   *
   * The shell cannot work the field ids out for itself: once composed, a branched template
   * is one flat step list with no seam, and `appendFirstStep` merges the branch's opening
   * sections into a base step so even the step boundary stops being one. The host holds
   * both templates, so it is the only side that knows.
   *
   * What the shell does with them is park this branch's answers when the advisor swaps
   * away, so swapping back restores them instead of asking the same questions twice. The
   * ids matter rather than being a nicety because a branch target is routinely a CLONE of
   * the base form, and clone preserves field ids by design: without the park-and-remove,
   * form A's answers would silently pre-fill form B's identical ids.
   */
  formBranch?: { targetTemplateId: string; owned: TemplateOwnedIds } | null;
  /**
   * The template the BMO snapshot is built against, when that differs from the one on
   * screen. Absent = {@link template}, which is every surface that does not branch.
   *
   * Only the SELECTED form's answers are persisted: a branched application sends the
   * branch target's fields and not the base form's retained prefix. `buildApplicationSnapshot`
   * walks exactly the steps it is handed and knows nothing about branching, so handing it a
   * different step list is the whole of that rule.
   */
  payloadTemplate?: WorkflowTemplate;
  /**
   * Which endpoint fills this workflow's PDFs.
   *
   * Absent = the authenticated route (`workflowsApi.generatePdfPreview`), which is what the
   * advisor runtime and originator/approver preview both want. The PUBLIC customer runtime
   * passes the anonymous one, because it has no session to authenticate with.
   *
   * This is the one place the default lives. `buildFilledPdfBlobUrl` deliberately takes the
   * generator as a REQUIRED argument with no fallback of its own, so a call site cannot
   * silently pick the authenticated client by omission.
   */
  generatePdf?: PdfGenerator;
  /**
   * Whether address fields may call the Canada Post proxy. Defaults to on.
   *
   * A CAPABILITY rather than a mode: it is about what this runtime is allowed to do, not
   * about whose runtime it is, which is why it does not ride on the mode union. The public
   * customer runtime passes false for three compounding reasons - every Find is a paid
   * Canada Post transaction fired on keystroke, the proxy is RequireFormRuntime-gated so an
   * anonymous caller gets a dead dropdown anyway, and PDF generation is the only request
   * that runtime may make. The fall-through is a plain text input, which is fully
   * functional: the customer types their address.
   */
  addressAutocomplete?: boolean;
  /**
   * Whether email inputs may call the ZeroBounce proxy on blur. Defaults to on.
   *
   * A CAPABILITY for exactly the reasons `addressAutocomplete` is one, and the public
   * customer runtime passes false for the same three compounding reasons: every verify is
   * a metered ZeroBounce transaction, the proxy is RequireFormRuntime-gated so an anonymous
   * caller would get nothing but a console breadcrumb per blur, and PDF generation is the
   * only request that runtime may make. The fall-through is the local RFC-5322 rule the
   * field already ran, which is fully functional: a customer who types a malformed address
   * is still told so.
   *
   * Independent of the e-signature step's own verification, which a public customer never
   * reaches at all (`OneSpanESign` is in `PUBLIC_HIDDEN_STEP_TYPES`).
   */
  emailVerification?: boolean;
};

/**
 * Two-column shell shared by Preview + Advisor: left rail listing every
 * DynamicForm step (with a blue check when every required field on that step
 * is filled), main column rendering the currently-selected step. Owns the
 * values map across step navigation so partially-filled steps keep their
 * input across rail clicks.
 *
 * In `'preview'` mode all rail entries are clickable regardless of completion;
 * in `'advisor'` mode (later) we'll gate navigation on sequential progression.
 *
 * Non-DynamicForm steps are filtered out — they don't have a "form" to
 * render. The user said only DynamicForm in the rail for now.
 */
export function FormShell({
  template,
  language: initialLanguage,
  mode,
  initialStepId,
  initialValues,
  isResumedApplication,
  onPrimaryClientNameChange,
  onSaveAndExit,
  onWelcomeCancel,
  applicationId,
  onSave,
  onSubmit,
  bmoStatus,
  creationTimestamp,
  readOnly,
  onOptionRedirect,
  onFormBranchChange,
  formBranch,
  payloadTemplate,
  generatePdf: generatePdfProp,
  addressAutocomplete,
  emailVerification,
}: Props) {
  const { t, i18n } = useTranslation();
  // `mode` narrowed for everything below the shell. Hoisted once so the six effects that
  // depend on it all reference one stable binding rather than each calling the mapper —
  // see formShellMode.ts for why the field tree keeps a two-member union.
  const rendererMode: RendererMode = rendererModeFor(mode);
  // Which steps this runtime is allowed to show, independent of the answers. Fixed for
  // the life of the mount; the condition rules that DO change live are composed with it
  // in the stepVisibility memo below.
  const audience: StepAudience = mode === 'public' ? 'public' : 'internal';
  // Resolved ONCE, here, and threaded to every consumer. buildFilledPdfBlobUrl takes the
  // generator as a required argument with no default precisely so this choice cannot be
  // made by omission at a call site; making it in one place is the other half of that.
  const generatePdf: PdfGenerator = generatePdfProp ?? workflowsApi.generatePdfPreview;
  // Which workflow a PDF fill is REQUESTED against. Same "resolved once, threaded to every
  // consumer" idiom as `generatePdf` directly above, and for a sharper reason: the server
  // validates the requested key against the steps of whatever workflow the URL names, so a
  // call site that reached for `template.id` on a branched form would 404 on every document
  // the branch owns. `template.id` IS the base form's here — `composeBranchedTemplate` pins
  // it there deliberately. See `buildFilledPdfBlobUrl`'s `requestTemplateId`.
  //
  // Read from `formBranch`, NOT from `payloadTemplate`. Both describe the branch, but
  // `composeBranchedPayloadTemplate` runs through the same `withBaseIdentity` override, so
  // `payloadTemplate.id` is the BASE form's id too — deriving this from it looks right and
  // silently resolves to the thing it is supposed to replace. `formBranch.targetTemplateId`
  // is the target's own id, and unlike `payloadTemplate` it is passed by every branching
  // surface rather than only by the one that saves.
  const pdfTemplateId = formBranch?.targetTemplateId ?? template.id;
  // Undefined outside advisor runtime, which is what stops originator/approver PREVIEW from
  // writing test files into the customer-PII bucket. See useAdditionalDocumentUpload.
  //
  // BOTH ids, and they do different jobs. `template.id` is where the file is posted and what gets
  // minted into its S3 key — an uploaded document belongs to the APPLICATION, which is identified
  // by the base form even after a branch fires, and that key is re-checked for ownership against
  // the base id by the status poll, the deliver call and submit's backstop alike.
  //
  // The branch target rides alongside because the ENTRY the file fills is the target's: the base
  // form's upload entries went with its steps below the branch point. Sending only the first was
  // DEV-SA-3 — every named entry on a subsequent form refused with "entryId does not match an
  // additional-document entry on this workflow", and Submit never enabled.
  const additionalDocumentUploader = useAdditionalDocumentUpload({
    templateId: template.id,
    // `formBranch`, not `payloadTemplate` — the same reason `pdfTemplateId` reads from it above:
    // `composeBranchedPayloadTemplate` pins its `id` to the base form's too, so deriving the
    // target from it silently resolves to the thing it is meant to replace.
    subsequentTemplateId: formBranch?.targetTemplateId ?? null,
    // Narrowed: the hook's own union is two-member, and a public runtime fails its
    // role check anyway (no session => no role) as well as having no applicationId.
    mode: rendererMode,
    applicationId,
  });
  // UI language — everything the advisor READS follows the app-level locale
  // (AppShell header switcher). Live-follows i18n.language so a header
  // toggle mid-form flips rail titles + field labels + chrome on the spot.
  // Previously a Welcome-type step's language radio flipped this too, which
  // meant the whole form re-rendered in the CLIENT's chosen language
  // instead of the advisor's — Lintao's fix.
  const language: 'en' | 'fr' = i18n.language.startsWith('fr') ? 'fr' : 'en';
  // The APPLICATION's language — the client's, not the advisor's. Drives which
  // PDF template gets pulled from S3 + filled at PdfGeneration (and the OneSpan
  // e-sign config's template pick, downstream), the locale of the labels in the
  // saved snapshot, and the `appLanguage` BMO stores for the application (see
  // `buildApplicationSnapshot`). Set by the Welcome step's radio when the
  // workflow shows one; otherwise it follows the advisor's UI locale (see the
  // sync effect below). Seeded from the mount-time `initialLanguage` prop.
  // Consumers that pick between `enFileKey` / `frFileKey` read this, not
  // `language`.
  const [pdfLanguage, setPdfLanguage] = useState<'en' | 'fr'>(initialLanguage);
  // Does this workflow put a language radio in front of the client? Only a
  // Welcome step that actually SHOWS its selector does — an originator can turn
  // the radio off (`showLanguageSelector: false`), which leaves the step in the
  // flow but gives the client nowhere to choose. When a radio exists it is the
  // sole authority on the PDF language and the sync effect below stays out of
  // the way; otherwise the language tracks the app-level locale. Gating on the
  // step's mere presence (as this did until now) froze `pdfLanguage` at the
  // mount-time locale for selector-off workflows, so a header switch mid-form
  // left both the generated PDF and the saved `appLanguage` on the old value.
  const hasLanguageSelector = useMemo(
    () =>
      template.steps.some(
        (s) => s.type === 'Welcome' && s.welcomeConfig?.showLanguageSelector !== false,
      ),
    [template.steps],
  );
  // No-radio fallback: keep the PDF language in lockstep with the advisor's
  // UI locale. Without this, `pdfLanguage` froze at whatever the locale was on
  // mount (useState only reads its initial arg once) and a header language
  // switch mid-form re-languaged the UI but silently left the generated PDF in
  // the old language. Guarded on `hasLanguageSelector` so a workflow WITH a
  // radio still lets the client's choice diverge from the advisor's UI.
  useEffect(() => {
    if (hasLanguageSelector) return;
    setPdfLanguage(language);
  }, [hasLanguageSelector, language]);
  /**
   * The advisor's flow ends at Document Upload — the generation step is folded away for them
   * (2026-10-02).
   *
   * It was a read-only list of filled PDFs whose only live control was Submit, and that control
   * now sits on the step before it (see `submitStep`). Nothing about submitting depended on
   * visiting it: BMO builds the package server-side, and the client receives the filled
   * documents through the e-signature package.
   *
   * Advisor ONLY. Originator/approver PREVIEW keeps the step for its per-row preview and
   * "Download all" bulk check, and the PUBLIC customer runtime keeps it because that download
   * IS the ending — there is no Submit there at all.
   */
  const foldsPdfStep = mode === 'advisor';
  // Advisor-facing rail. PdfFileUpload is excluded outright — those are purely
  // originator-side source-template configuration the advisor never sees — and
  // PdfGeneration joins it in advisor runtime (see `foldsPdfStep`). The advisor
  // walks every other type in order:
  //   DynamicForm → ApplicationReview → OneSpanESign → AdditionalDocuments → Submit
  // (post-2026-06-17 flow redesign — Application Review + flipped order).
  //
  // This is the ONE place the step has to disappear from: the rail, `stepVisibility`,
  // `visibleSteps`, `prevStep`/`nextStep`, `stepCompletion`, `railLocked` and
  // `deriveResumePosition` all read `allSteps`. It is also safe for the PDFs themselves —
  // every consumer of the generation step's `fileEntries` (`applicablePdfDocs`,
  // `collectGeneratedPdfEntryIds`, ApplicationReviewStep's own `priorSteps` slice) reads the
  // raw `template`, never this list.
  const allSteps = useMemo(
    () =>
      template.steps.filter(
        (s) => s.type !== 'PdfFileUpload' && !(foldsPdfStep && s.type === 'PdfGeneration'),
      ),
    [template.steps, foldsPdfStep],
  );
  // Every section of the workflow — how a "repeat once per instance of" section finds the one it
  // mirrors, and how per-instance conditions reach across that link (`scopeValuesToInstance`).
  const allSections = useMemo(() => allSectionsOf(template), [template]);
  const hasMirroredSections = useMemo(
    () => allSections.some((s) => mirrorSourceOf(s, allSections) !== null),
    [allSections],
  );

  // No browser storage. Values + nav state live in React memory only — the
  // BMO integration will own real persistence later (every ~20s autosave +
  // on Next + on Save and Exit, dispatching to BMO's backend). Until that
  // ships the advisor session is in-tab only; closing the tab loses work.
  // Documented tradeoff per user 2026-06-18.
  const seededStepId = (() => {
    if (initialStepId && allSteps.some((s) => s.id === initialStepId)) return initialStepId;
    return allSteps[0]?.id ?? null;
  })();
  const [activeStepId, setActiveStepId] = useState<string | null>(seededStepId);
  /** The shell's own scroll container — reset to the top on every step change. */
  const mainRef = useRef<HTMLElement | null>(null);
  // A FRESH application starts with its "Selected by default" answers in place (QA D-17 / D-42),
  // on every step — nothing has been shown yet, including the step about to render, so this is
  // the one moment the whole workflow can be filled without asking what the advisor has seen.
  // A RESUMED application is left exactly as saved here; the gated pass further down fills only
  // steps it never reached.
  const [values, setValues] = useState<Record<string, FieldValue>>(() =>
    isResumedApplication
      ? (initialValues ?? {})
      : applyDefaultOptions(template, initialValues ?? {}, { clientLanguage: initialLanguage }),
  );
  // In-flight BMO save. Disables the nav actions so a double-click can't queue two saves of
  // two different snapshots and let the older one land last.
  const [saving, setSaving] = useState(false);
  // In-flight final submit — separate from `saving` so the Submit button's own spinner doesn't
  // depend on (or block behind) an unrelated Save-and-exit click.
  const [submitting, setSubmitting] = useState(false);
  // The submit failed for something the advisor cannot fix, and the GAMX support block is up.
  // Its own flag rather than a derived one: the dialog outlives the press that raised it (the
  // chain has settled, `submitting` is already back to false) and only the advisor closes it.
  const [submitErrorOpen, setSubmitErrorOpen] = useState(false);
  // Which copy that dialog carries — the GAMX support block, or "sign in again". Separate from
  // the open flag so the variant survives the render that opens it.
  const [submitErrorVariant, setSubmitErrorVariant] = useState<'technical' | 'session'>(
    'technical',
  );
  // In-flight Next. Separate from `saving` because it also covers the half of the advance that
  // is not a save: preview has no `onSave` at all, and there the whole cost of a Next is the
  // incoming step's render — long enough on a big workflow that the previewer clicks and
  // watches nothing happen. Drives the Next button's spinner; cleared by the step actually
  // changing (see below), not by the handler returning.
  const [advancing, setAdvancing] = useState(false);
  // Where this application sits on BMO's status ladder (see ./bmoStatus). The REF is the source
  // of truth and the state is only its render mirror: persistSnapshot has to read the status at
  // the instant it fires, and a demotion landing between a Next click and its `await` must be
  // included — a value captured in the render closure would miss it.
  //
  // Seeded from BMO's confirmed value when there is one, otherwise from whether the application
  // EXISTS: a `/new` application starts on the bottom rung (APPLICATION_INITIATED) because the
  // save that follows is the one that creates it, while one reached by id starts IN-PROGRESS.
  // See `initialStatusFor` — resume answers 501 until B5 / B15, so "no confirmed status" is the
  // common path for both and the id is the only thing telling them apart.
  const bmoStatusRef = useRef<string>(bmoStatus ?? initialStatusFor(applicationId));
  const [status, setStatusState] = useState<string>(bmoStatusRef.current);
  const commitStatus = useCallback((compute: (prev: string) => string) => {
    const next = compute(bmoStatusRef.current);
    if (next === bmoStatusRef.current) return;
    bmoStatusRef.current = next;
    setStatusState(next);
  }, []);
  // BMO taking the application over — the advisor loses the pen mid-session and the shell has to
  // notice on the spot. Only LOCKED values are adopted: the echo from an ordinary save just
  // repeats the rung we sent, and adopting that back would undo a demotion the advisor earned in
  // the gap between the save leaving and the response landing.
  useEffect(() => {
    if (bmoStatus && isLocked(bmoStatus)) commitStatus(() => bmoStatus);
  }, [bmoStatus, commitStatus]);
  // Read-only for either reason: the caller asked, or BMO owns the application now.
  const locked = readOnly === true || isLocked(status);
  // Advisor-uploaded documents from any AdditionalDocuments step. Hoisted
  // here (rather than inside AdditionalDocumentsStep) so the state
  // survives step navigation AND so the downstream PdfGeneration step
  // can list them alongside the filled PDF templates, and the "Download
  // all" button can zip both into a single advisor deliverable. Keyed
  // by entryId for named mandatory/optional entries + a `bucket` array
  // for the free-form optional bucket.
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFilesStore>(EMPTY_UPLOADS);
  // Malware scan verdicts for those uploads (ADR-0024). Lives HERE, beside the store it patches,
  // rather than inside AdditionalDocumentsStep: a file uploaded on the documents step has to keep
  // resolving while the advisor works through later steps, which is the entire point of showing
  // scan state during form-filling instead of surfacing it as an error at submit. Mounted on the
  // step, the loop would die on every navigation away.
  //
  // Gated on the uploader's presence for the same reason the uploader itself is gated: preview
  // never uploads, so it has nothing to poll for.
  useAdditionalDocumentScanStatus({
    templateId: template.id,
    enabled: additionalDocumentUploader !== undefined,
    uploadedFiles,
    onUploadedFilesChange: setUploadedFiles,
  });
  // Track which fields the advisor has typed into — used to sever
  // `sourceField` prefill links. Once a field is in this set, the prefill
  // effect stops overwriting it even if the source field's value later
  // changes. Cleared implicitly when FormShell unmounts.
  const editedFieldsRef = useRef<Set<string>>(new Set());
  // Steps the advisor has been shown. A cross-step prefill stops following its source once the
  // target's step is in here: the advisor has seen that value, so a later change to the source
  // must not quietly rewrite it (QA D-38 — editing the account holder's city changed the
  // beneficiary's). Seeded with the active step at mount, and kept current by the effect below.
  const shownStepsRef = useRef<Set<string>>(new Set(activeStepId ? [activeStepId] : []));
  useEffect(() => {
    if (activeStepId) shownStepsRef.current.add(activeStepId);
  }, [activeStepId]);
  // The answers a resumed application came back with. Its prefilled fields were saved as the
  // advisor left them — possibly corrected — and `editedFieldsRef` starts empty, so without this
  // reopening would overwrite them from their sources.
  const restoredValuesRef = useRef(isResumedApplication ? (initialValues ?? {}) : {});
  // What the prefill effect itself wrote, per target key — so a copy can be WITHDRAWN when its
  // gate closes before the advisor ever saw it (Reg/Non-Reg QA obs. 7: SWP bank copied into PAC,
  // SWP switched back to No, PAC still opened with the SWP bank).
  const prefillCopiesRef = useRef<Map<string, FieldValue>>(new Map());
  // Of those copies, the ones written while the target was INERT — read-only, or greyed out by a
  // "Disable if" rule. The advisor could not have corrected such a value, so having seen it does
  // not make it theirs, and the copy stays withdrawable when its gate closes (the sub-agreements'
  // "are the subscriber and primary contact the same?" sits on the step whose fields it mirrors).
  const mirrorCopyKeysRef = useRef<Set<string>>(new Set());
  // Bumped whenever an advisor edit hits `editedFieldsRef`, so downstream
  // useMemos that depend on the edited set can revalidate (React can't see
  // a mutation on a ref).
  const [editedTick, setEditedTick] = useState(0);

  // Rail overlay open/close — the ONLY responsive-related JS state. Whether the
  // rail collapses at all is decided purely in CSS (`@container` on
  // `.form-shell`, see theme/global.css); this just tracks the slide-in toggle
  // when it's collapsed, surfaced to CSS via the `--rail-open` modifier class.
  const [railOpen, setRailOpen] = useState(false);

  // Step-level Next blocker set by the currently-rendered step content
  // (INFLOWS-188: OneSpanESignStep blocks Next when any signatory email
  // fails ZeroBounce verification). Value is the tooltip message to show
  // on the disabled button; null means the step isn't blocking anything.
  // Cleared automatically on step switch — the StepContent's key={id}
  // remount unmounts the OneSpan effect that sets it.
  const [stepBlocker, setStepBlocker] = useState<string | null>(null);

  // Session-scoped prefill for fields carrying `sourceValue` (and for the
  // `advisorRepCodeSelector`). Runs when the /me session lands, over EVERY
  // field on EVERY step. `decideAdvisorPrefill` owns the policy — read its doc
  // comment for why the live session outranks a restored value, why it stops
  // once the application is filed, and what stays the advisor's.
  //
  // `status` is a dependency because the gate reads it: an application that
  // goes locked mid-session must stop being refreshed from that point on. That
  // re-run is also why `editedFieldsRef` is consulted below — passing a step
  // changes the status, and the pass must not take back a correction the
  // advisor made on the step they just left. No `editedTick` dependency: the
  // ref is read at the moment the pass runs, and re-running on every keystroke
  // would be churn for a set of slots that are almost all read-only anyway.
  //
  // Otherwise rate-limited by useSession's module-level cache.
  const { user } = useSession();
  useEffect(() => {
    if (!user) return;
    setValues((prev) => {
      let next: Record<string, FieldValue> = prev;
      for (const step of template.steps) {
        if (step.type !== 'DynamicForm') continue;
        for (const section of step.sections) {
          // §1 Repeatable Sections: prefill EVERY current instance's
          // qualified slot when the section is repeatable. Otherwise
          // fall through to the bare id (one iteration).
          const instanceCount = section.repeatable
            ? getInstanceCount(next, section.id, sectionMinInstances(section))
            : 1;
          for (const row of section.rows) {
            for (const col of row.columns) {
              const field = col.field;
              if (!field || !advisorSourceKeyOf(field)) continue;
              for (let i = 0; i < instanceCount; i++) {
                const key = section.repeatable ? `inst:${section.id}:${i}:${field.id}` : field.id;
                const action = decideAdvisorPrefill(
                  field,
                  user,
                  { current: next[key], edited: editedFieldsRef.current.has(key) },
                  { mode: rendererMode, status },
                );
                if (action.kind === 'leave') continue;
                if (next === prev) next = { ...prev };
                // `clear` writes null, not '': an emptied slot has to drop OUT
                // of the next snapshot (buildApplicationSnapshot omits null),
                // where '' would tell BMO the advisor answered with a blank.
                next[key] = action.kind === 'set' ? action.value : null;
              }
            }
          }
        }
      }
      return next;
    });
  }, [user, template, rendererMode, status]);

  // Workflow-variable prefill for fields carrying `sourceValue: 'variable.<key>'`.
  //
  // A SEPARATE effect from the session pass above, and not merely for tidiness: that one
  // returns early on `!user`, because every key it resolves is a fact about the signed-in
  // advisor. A variable is a fact about the WORKFLOW and needs no session at all, so folding
  // this in there would silently skip the whole pass for the public customer runtime — the
  // audience with no session by definition. `decideVariablePrefill` owns the policy; read its
  // doc comment for why the workflow's value outranks a restored one and what stays the
  // advisor's.
  //
  // `status` is a dependency for the same reason as above: an application that goes locked
  // mid-session must stop being refreshed from that point on, and `editedFieldsRef` is
  // consulted so that re-run cannot take back a correction made on a step just left.
  const templateVariables = template.variables;
  useEffect(() => {
    setValues((prev) => {
      let next: Record<string, FieldValue> = prev;
      for (const step of template.steps) {
        if (step.type !== 'DynamicForm') continue;
        for (const section of step.sections) {
          // Repeatable sections prefill every current instance's qualified slot; a plain
          // section falls through to the bare field id in one iteration. Same walk as the
          // session pass — see its §1 note.
          const instanceCount = section.repeatable
            ? getInstanceCount(next, section.id, sectionMinInstances(section))
            : 1;
          for (const row of section.rows) {
            for (const col of row.columns) {
              const field = col.field;
              if (!field || !isVariableSourcedField(field)) continue;
              for (let i = 0; i < instanceCount; i++) {
                const key = section.repeatable ? `inst:${section.id}:${i}:${field.id}` : field.id;
                const action = decideVariablePrefill(
                  field,
                  templateVariables,
                  { current: next[key], edited: editedFieldsRef.current.has(key) },
                  { mode: rendererMode, status },
                );
                if (action.kind === 'leave') continue;
                if (next === prev) next = { ...prev };
                next[key] = action.value;
              }
            }
          }
        }
      }
      return next;
    });
  }, [template, templateVariables, rendererMode, status]);

  // "Repeat once per instance of" (QA D-39): a mirrored section always holds as many instances as
  // the section it follows — Accountholder 2 added, a second relationship block appears. Only the
  // count sentinel is written, which every reader already goes through (see
  // `syncMirroredInstanceCounts`); removing an instance is paired in `removeInstance`.
  useEffect(() => {
    if (!hasMirroredSections) return;
    setValues((prev) => syncMirroredInstanceCounts(template, prev, pdfLanguage));
  }, [hasMirroredSections, template, values, pdfLanguage]);

  // Cross-step field prefill (§2). On every values change, walk every
  // DynamicForm field carrying a `sourceField`: if the target is still
  // empty AND the advisor hasn't manually edited it AND the source is
  // VISIBLE with a non-empty value, copy the source into the target. Advisor
  // typing severs the link via `editedFieldsRef`. The effect runs whenever the
  // source's value changes so subsequent typing on an unedited target
  // still pulls the latest source. Compared strictly by value + edit-set;
  // `editedTick` is the state-visible witness to ref mutations.
  //
  // The visibility gate (`isSourceVisible`) exists because hidden answers are
  // deliberately RETAINED in the values map so toggling a branch doesn't lose work
  // (spec §6.4). Without it the prefill happily read a step the advisor had closed
  // and carried its answer onto a step that IS visible — past the hidden-step pruning
  // in `buildApplicationSnapshot` / `visibleValues`, and so into the PDF and the BMO
  // payload. Clearing the source instead (`clearHiddenAnswers`) does not help: the
  // copy is taken the instant the source is typed, long before the target's step is
  // reached, and a blank source is skipped rather than withdrawn.
  useEffect(() => {
    // Build a lookup of each authored field's containing section, so we can
    // resolve BOTH sides of a sourceField relationship to a qualified key
    // when either side lives inside a repeatable section. The source
    // resolves to instance 0 (the primary answer) unless the prefill is
    // GATED: then the first source instance whose conditions pass is the
    // one copied (QA D-15 / D-22 — Caregiver 1 from whichever holder is
    // the primary caregiver; see resolvePrefillSourceInstance).
    const sectionByField = new Map<string, FormSection>();
    const stepByField = new Map<string, WorkflowStep>();
    const fieldById = new Map<string, WorkflowField>();
    for (const step of template.steps) {
      if (step.type !== 'DynamicForm') continue;
      for (const section of step.sections) {
        for (const row of section.rows) {
          for (const col of row.columns) {
            if (!col.field) continue;
            sectionByField.set(col.field.id, section);
            stepByField.set(col.field.id, step);
            fieldById.set(col.field.id, col.field);
          }
        }
      }
    }
    const keyFor = (fieldId: string, instanceIdx: number): string => {
      const sec = sectionByField.get(fieldId);
      if (!sec || !sec.repeatable) return fieldId;
      return `inst:${sec.id}:${instanceIdx}:${fieldId}`;
    };
    // `hiddenBorrowedFieldIds` walks an entire step, and this effect runs on every
    // keystroke, so the per-step answer is cached for as long as the values map it was
    // computed from stays identical. `next` only gains a new identity when a copy is
    // actually written, which is rare — so in the common case this is one walk per step.
    let borrowedCache = new Map<string, Set<string>>();
    let borrowedCacheFor: Record<string, FieldValue> | null = null;
    const hiddenBorrowedIn = (step: WorkflowStep, vals: Record<string, FieldValue>) => {
      if (borrowedCacheFor !== vals) {
        borrowedCache = new Map();
        borrowedCacheFor = vals;
      }
      let ids = borrowedCache.get(step.id);
      if (!ids) {
        ids = hiddenBorrowedFieldIds(step, vals);
        borrowedCache.set(step.id, ids);
      }
      return ids;
    };
    /**
     * Is a field currently on screen? One that the advisor has hidden — by answering
     * something earlier that closed its step, its section, its own field condition or the
     * reveal option that borrows it — must not feed a target, and any copy already taken
     * from it must be handed back (Reg/Non-Reg QA obs. 7 generalised: the SWP bank reached
     * PAC through the STEP's condition, not the prefill's own gate).
     *
     * Audience counts too: without it a prefill sourced from an `advisorOnly` step would
     * copy the advisor's answer into a field the public customer can see.
     *
     * Evaluated against the in-flight `next`, not the memoized `stepVisibility` map, so it
     * agrees with every other read in this effect.
     */
    const isOnScreen = (
      step: WorkflowStep,
      section: FormSection,
      field: WorkflowField,
      vals: Record<string, FieldValue>,
      // Values the FIELD's own conditions read. The TARGET side passes the instance-scoped
      // view, because a reveal inside a repeatable section is answered per instance; the
      // source side passes `vals` unchanged, which is what it has always evaluated.
      fieldCondValues: Record<string, FieldValue> = vals,
    ): boolean => {
      if (!isStepEligibleForAudience(step, audience)) return false;
      if (!evaluateConditions(step.conditions, step.conditionCombinator, vals)) return false;
      if (!evaluateConditions(section.conditions, section.conditionCombinator, vals)) return false;
      if (!evaluateConditions(field.conditions, field.conditionCombinator, fieldCondValues))
        return false;
      // A reveal target carries no conditions of its own — its visibility belongs to the
      // option that borrows it, so it can only be resolved through this walk.
      return !hiddenBorrowedIn(step, vals).has(field.id);
    };
    /**
     * Is a prefill's SOURCE on screen? An unresolvable source fails OPEN (visible) — a
     * dangling `sourceField` then behaves exactly as it did before this gate existed,
     * rather than silently going dead.
     */
    const isSourceVisible = (sourceFieldId: string, vals: Record<string, FieldValue>): boolean => {
      const step = stepByField.get(sourceFieldId);
      const section = sectionByField.get(sourceFieldId);
      const field = fieldById.get(sourceFieldId);
      if (!step || !section || !field) return true;
      return isOnScreen(step, section, field, vals);
    };
    /**
     * Is this TARGET inert — authored `readOnly`, or disabled by a "Disable if" rule at
     * step, section or field level, exactly as FormRenderer computes it
     * (most-restrictive-wins, the field's own rule read per instance)?
     *
     * An inert prefill target never stops following its source. The freeze below exists
     * because a value the advisor has SEEN may be a value they corrected (QA D-38), but an
     * input they cannot type into affords no correction: it is ours for as long as it stays
     * inert, so it re-syncs every time the source changes. That is what makes a "same as
     * above" style rule work — tick it, the mirrored fields grey out and track the original,
     * including whatever the advisor had typed into them before ticking.
     *
     * Authored `readOnly` belongs here for the same reason, and only reached the list once
     * a same-step mirror existed to prove it: the Annex A dealer-name box on the
     * sub-agreements is read-only and sources the dealer name typed a section above it, so
     * with the freeze applied it was still empty the moment its step was opened and never
     * caught up. A cross-step read-only mirror hid the gap — its step was not yet shown when
     * the copy was taken, so the freeze had nothing to bite on.
     *
     * Being HIDDEN is deliberately NOT the same thing, which is why `isOnScreen` is a
     * precondition at the call site rather than a courtesy: a hidden answer is dropped on
     * submit and the advisor cannot watch it change, so it keeps the normal freeze.
     *
     * The application's own locked/read-only state is also NOT this: `locked` greys out
     * every step of a filed application, and re-syncing a filed application's answers is
     * the opposite of what that means.
     */
    const isTargetRuleDisabled = (
      step: WorkflowStep,
      section: FormSection,
      field: WorkflowField,
      vals: Record<string, FieldValue>,
      fieldCondValues: Record<string, FieldValue>,
    ): boolean =>
      field.readOnly === true ||
      isDisabledByRule(step, vals) ||
      isDisabledByRule(section, vals) ||
      isDisabledByRule(field, fieldCondValues);
    setValues((prev) => {
      let next: Record<string, FieldValue> = prev;
      for (const step of template.steps) {
        if (step.type !== 'DynamicForm') continue;
        for (const section of step.sections) {
          const targetInstances = section.repeatable
            ? getInstanceCount(next, section.id, sectionMinInstances(section))
            : 1;
          for (const row of section.rows) {
            for (const col of row.columns) {
              const field = col.field;
              if (!field?.sourceField) continue;
              // A field can share a uniqueness group with its own prefill source (RDSP's
              // beneficiary email copies the holder's when the beneficiary IS the holder). It
              // is copied like any other: uniqueness exempts the (source instance → target
              // instance) pair this effect actually writes (`isActivePrefillCopy` in
              // uniqueness.ts, which re-runs `resolvePrefillSourceInstance` below), so the copy
              // cannot be refused — but a value the advisor typed under a gate that did NOT
              // pass is compared like any other.
              // Which source instance to copy — 0 for an ungated prefill; for a
              // gated one the first instance its conditions pass for, or none,
              // in which case the target is left exactly as it is.
              //
              // A source that is not on screen is treated exactly like a gate that does
              // not pass: nothing new is copied, and an unseen copy already taken is
              // withdrawn below. Routing it through the same `null` keeps ONE withdrawal
              // path, so a prefill gets this for free without the author having to restate
              // the source step's condition on the prefill itself.
              const sourceIdx = isSourceVisible(field.sourceField.fieldId, next)
                ? resolvePrefillSourceInstance(
                    field.sourceField,
                    sectionByField.get(field.sourceField.fieldId),
                    next,
                    allSections,
                  )
                : null;
              // "Caregiver 1 only": a target instance narrows the write to that one
              // instance of the target's repeatable section. Unset = every instance.
              const onlyInstance = section.repeatable
                ? (field.sourceField.targetInstance ?? null)
                : null;
              // Carries the instance index alongside the key: the target's disable rule and
              // its own visibility condition are both read per instance.
              const targets: { key: string; instance: number }[] = [];
              for (let i = 0; i < targetInstances; i++) {
                if (onlyInstance !== null && i !== onlyInstance) continue;
                targets.push({
                  key: section.repeatable ? `inst:${section.id}:${i}:${field.id}` : field.id,
                  instance: i,
                });
              }
              // Is this target instance a live MIRROR of its source — on screen and disabled by
              // rule, so the advisor cannot touch it and it is ours for as long as the rule
              // holds? Stronger than `stillOurs` below in one way that matters: a mirror also
              // follows its source DOWN to empty.
              const mirrorsSource = (instance: number) => {
                const fieldCondValues = section.repeatable
                  ? scopeValuesToInstance(section, next, instance, allSections)
                  : next;
                return (
                  isOnScreen(step, section, field, next, fieldCondValues) &&
                  isTargetRuleDisabled(step, section, field, next, fieldCondValues)
                );
              };
              // A value the advisor never saw, never typed over and did not restore is still OURS
              // to change; once any of those holds, it is theirs (QA D-38) — unless it is a mirror.
              const stillOurs = (targetKey: string, instance: number) =>
                mirrorsSource(instance) ||
                (!editedFieldsRef.current.has(targetKey) &&
                  !shownStepsRef.current.has(step.id) &&
                  !isNonEmptyValue(restoredValuesRef.current[targetKey]));
              // Withdrawal asks a weaker question than the copy above, because the copy it is
              // taking back was written into a target the advisor could not type into. The
              // D-38 freeze protects a value they SAW and may have accepted as-is — but a
              // mirror affords no accepting, so being shown one says nothing, and the gate
              // closing is the advisor's own instruction about these very fields. The
              // sub-agreements now ask "are the subscriber and primary contact the same?"
              // directly above the mirrored address it gates: answer yes then no, and without
              // this the subscriber's address stays behind in a Primary Contact box that has
              // just been declared to be somebody else's. An editable target (Reg/Non-Reg's
              // PAC bank, prefilled from SWP) was never a mirror and keeps the full freeze.
              const stillOursToWithdraw = (targetKey: string, instance: number) =>
                mirrorCopyKeysRef.current.has(targetKey) || stillOurs(targetKey, instance);
              const forget = (targetKey: string) => {
                prefillCopiesRef.current.delete(targetKey);
                mirrorCopyKeysRef.current.delete(targetKey);
              };
              if (sourceIdx === null) {
                // The gate closed: take back the copy we made, leave anything else alone.
                for (const { key: targetKey, instance } of targets) {
                  const copied = prefillCopiesRef.current.get(targetKey);
                  if (
                    copied === undefined ||
                    !stillOursToWithdraw(targetKey, instance) ||
                    next[targetKey] !== copied
                  )
                    continue;
                  if (next === prev) next = { ...prev };
                  next[targetKey] = null;
                  forget(targetKey);
                }
                continue;
              }
              // Source lives under either its bare id or under its
              // section's qualified key for that instance.
              const sourceKey = keyFor(field.sourceField.fieldId, sourceIdx);
              const sourceValue = next[sourceKey];
              if (
                sourceValue === undefined ||
                sourceValue === null ||
                (typeof sourceValue === 'string' && sourceValue.trim().length === 0)
              ) {
                // An empty source is still an ANSWER to a mirror: the advisor clears the
                // holder's middle name, and the disabled copy of it has to clear too, or the
                // form ships a value nobody can see is wrong and nobody can edit. The gate
                // passed and the source is on screen, so this is "the thing I mirror is
                // empty" — NOT the gate-closed case above, where the prefill has no source
                // to speak for and the target's value may well have come from somewhere else.
                for (const { key: targetKey, instance } of targets) {
                  if (!mirrorsSource(instance)) continue;
                  const current = next[targetKey];
                  if (current === undefined || current === null) continue;
                  if (next === prev) next = { ...prev };
                  next[targetKey] = null;
                  forget(targetKey);
                }
                // Any OTHER target keeps what it has: there is no meaningful value to
                // propagate yet, and an editable target may be sitting on an authored
                // default that "" would quietly wipe.
                continue;
              }
              for (const { key: targetKey, instance } of targets) {
                // Advisor typing into THIS target severs the prefill link.
                // editedFieldsRef records the SAME key shape onChange emits
                // (the qualified instance key for a repeatable field, the
                // bare id otherwise), so match on targetKey — a bare-id
                // check would miss per-instance edits and clobber them.
                // Already shown to the advisor, or restored from a save: the value stays as it is.
                if (!stillOurs(targetKey, instance)) continue;
                const current = next[targetKey];
                // Already mirrors the source — no-op to avoid churn and let
                // the effect settle without another values change.
                if (current === sourceValue) continue;
                // Keep the unedited target in sync with the LATEST source
                // value, overwriting any earlier partial copy. The previous
                // "only write while the target is empty" guard froze the
                // target at whatever the source held on the first effect run
                // — typically the first word the advisor had typed — and
                // never caught up as they finished the rest of the value.
                if (next === prev) next = { ...prev };
                next[targetKey] = sourceValue;
                prefillCopiesRef.current.set(targetKey, sourceValue);
                // Remember whether the target was inert when we wrote into it — that, not
                // whether its step has been shown, is what makes the copy ours to take back
                // if the gate later closes. Re-evaluated on every write, so a target that
                // stops being a mirror while still holding our copy drops out of the set.
                if (mirrorsSource(instance)) mirrorCopyKeysRef.current.add(targetKey);
                else mirrorCopyKeysRef.current.delete(targetKey);
              }
            }
          }
        }
      }
      return next;
    });
    // editedTick is intentionally in the dep set — mutations to the ref
    // itself don't re-run the effect, so we bump the tick when an edit
    // is recorded to let this effect re-observe the frozen set.
  }, [template, values, editedTick, allSections, audience]);
  // "Same as the Welcome page's language" (QA RIF TC-07-01, the legacy RIF behaviour): a RadioGroup
  // carrying `sourceValue: 'application.language'` starts on the language picked on the Welcome card.
  // Same freeze rules as the cross-step prefill — it follows the Welcome choice only until its step has
  // been shown, edited or restored; after that the answer is the advisor's.
  useEffect(() => {
    setValues((prev) => {
      let next = prev;
      for (const step of template.steps) {
        if (step.type !== 'DynamicForm' || shownStepsRef.current.has(step.id)) continue;
        for (const section of step.sections) {
          if (section.repeatable) continue;
          for (const row of section.rows)
            for (const col of row.columns) {
              const f = col.field;
              if (f?.sourceValue !== 'application.language') continue;
              if (
                editedFieldsRef.current.has(f.id) ||
                isNonEmptyValue(restoredValuesRef.current[f.id])
              )
                continue;
              if (!f.options.some((o) => o.value === pdfLanguage) || prev[f.id] === pdfLanguage)
                continue;
              if (next === prev) next = { ...prev };
              next[f.id] = pdfLanguage;
            }
        }
      }
      return next;
    });
  }, [template, pdfLanguage, activeStepId]);
  // "Start with as many rows as that table" (QA Reg/Non-Reg D-22): the PAC fund table opens with one
  // blank row per SWP fund, as the legacy form did. Same freeze rules as the field prefill above —
  // never once the table's step has been shown, edited or restored — and it only ever ADDS blank
  // rows to a table nobody has typed into.
  useEffect(() => {
    setValues((prev) => {
      let next = prev;
      for (const step of template.steps) {
        if (step.type !== 'DynamicForm' || shownStepsRef.current.has(step.id)) continue;
        for (const section of step.sections)
          for (const row of section.rows)
            for (const col of row.columns) {
              const field = col.field;
              if (field?.type !== 'RepeatableRow' || !field.rowsFollowFieldId) continue;
              if (
                editedFieldsRef.current.has(field.id) ||
                isNonEmptyValue(restoredValuesRef.current[field.id])
              )
                continue;
              const source = prev[field.rowsFollowFieldId];
              const wanted = Array.isArray(source)
                ? source.filter(
                    (r) =>
                      r && Object.values(r as Record<string, FieldValue>).some(isNonEmptyValue),
                  ).length
                : 0;
              const current = Array.isArray(prev[field.id])
                ? (prev[field.id] as Record<string, FieldValue>[])
                : [];
              if (wanted <= current.length) continue;
              if (current.some((r) => r && Object.values(r).some(isNonEmptyValue))) continue;
              const blank = () =>
                Object.fromEntries((field.repeatableTemplate ?? []).map((c) => [c.id, null]));
              const cap = field.maxRows ?? wanted;
              if (next === prev) next = { ...prev };
              next[field.id] = Array.from({ length: Math.min(wanted, cap) }, blank) as FieldValue;
            }
      }
      return next;
    });
  }, [template, values, editedTick]);
  // An option the client's language does not offer cannot stay answered (QA D-30): when the
  // Welcome step's language flips under an answer — "Ms." picked, then French chosen — the
  // answer is cleared, so it never reaches the review, the snapshot or a PDF with no box for
  // it. Same rule `pruneStaleChoices` applies to an option a newer version removed; it returns
  // the same map when nothing is cleared, so this settles after one pass. Skipped outright for
  // a workflow that scopes no option to a language.
  // The language an option scope is judged against: French when EITHER the client's PDF or the
  // advisor's screen is French. QA switched to French with the header and still saw the
  // English-only "Ms." (round 3); a French-only option keeps showing for a French client.
  const offerLanguages: 'en' | 'fr' = pdfLanguage === 'fr' || language === 'fr' ? 'fr' : 'en';
  const hasLanguageScopedOptions = useMemo(
    () =>
      template.steps.some((s) =>
        (s.sections ?? []).some((sec) =>
          sec.rows.some((r) =>
            r.columns.some((c) => c.field?.options?.some((o) => (o.languages?.length ?? 0) > 0)),
          ),
        ),
      ),
    [template.steps],
  );
  useEffect(() => {
    if (!hasLanguageScopedOptions) return;
    setValues((prev) => pruneStaleChoices(template, prev, { language: offerLanguages }).values);
  }, [hasLanguageScopedOptions, template, offerLanguages, values]);
  // Filled-PDF blob URL cache — survives step navigation because it lives on
  // FormShell (which stays mounted across step changes). Keyed by
  // StepFileEntry.id; each entry stores the `values` reference the URL was
  // built from so PdfGenerationStep can detect a stale cache when the
  // advisor edits an answer and revisits the final step. FormShell's own
  // unmount revokes everything.
  const pdfCacheRef = useRef<PdfCache>(new Map());
  useEffect(
    () => () => {
      pdfCacheRef.current.forEach(({ url }) => URL.revokeObjectURL(url));
      pdfCacheRef.current.clear();
    },
    [],
  );
  // Steps the advisor has Next'd OFF OF *and nothing since has invalidated*. A step only paints
  // the rail's blue check after they've explicitly advanced past it (not the moment the renderer
  // thinks it's vacuously "complete" — e.g. a step with no required fields would otherwise show
  // the check the moment it lands in the workflow, which surprised an originator reviewing the
  // BMO Carlyle smartform's Accredited Investor step).
  //
  // The second half of that sentence is newer and is the part a reader will not expect: the set
  // is NOT monotonic. An edit revokes every step AFTER the edited one (see
  // `demoteForActiveStep`), because an advisor who changes an answer has to walk the ladder
  // again rather than rail-clicking straight back to where they were. Forward-only, so they are
  // never locked out from behind.
  const [advancedSteps, setAdvancedSteps] = useState<Set<string>>(() => new Set());
  // Steps the advisor has STOOD ON, which is a weaker thing than having Next'd off one and is
  // tracked separately because the two drive different surfaces: `advancedSteps` paints the blue
  // check (see above — ticking a step the advisor merely visited is the bug that comment
  // describes), while this set is what the rail's lock consults.
  //
  // Splitting them fixes BMO #314. The lock used to read `advancedSteps` alone, so the furthest
  // step reached was never in it — a step joins that set only on the way OUT — and stayed
  // clickable purely by the `s.id !== activeStepId` escape hatch in `railLocked`. Pressing Back
  // closed the hatch and re-locked the step the advisor had just been editing, with no edit and
  // nothing invalidated: they had to Next through every page in between to return to it.
  //
  // Invalidation still applies. `demoteForActiveStep` truncates this set exactly as it truncates
  // `advancedSteps`, so an edit revokes the steps after it here too and the advisor still walks
  // the ladder forward one Next at a time rather than rail-clicking past the save that Next
  // performs.
  const [visitedSteps, setVisitedSteps] = useState<Set<string>>(() => new Set());
  // One writer for every way the active step can change — Next, Back, a rail click, the resume
  // jump, goToThankYou, a condition bouncing the advisor off a step that just went hidden. An
  // effect rather than edits at those call sites precisely because there are that many of them:
  // a future navigation route gets this for free instead of silently re-locking its destination.
  useEffect(() => {
    if (!activeStepId) return;
    setVisitedSteps((prev) => (prev.has(activeStepId) ? prev : new Set(prev).add(activeStepId)));
  }, [activeStepId]);
  // What the rail lock actually reads. `advancedSteps` is not a subset of `visitedSteps`: a
  // resume seeds it with `priorStepIds` walked in an EARLIER session, and `afterEditTarget` adds
  // the steps a post-edit return to Review skipped OVER. Both are legitimately reachable and
  // neither was ever the active step in this mount, so the union — not `visitedSteps` alone — is
  // the honest answer to "has the advisor been able to get here".
  const reachedSteps = useMemo(() => {
    const next = new Set(visitedSteps);
    for (const id of advancedSteps) next.add(id);
    return next;
  }, [visitedSteps, advancedSteps]);
  // INFLOWS-443: an EDIT pressed on Application Review comes back to it. Remembers the Review step and
  // which steps were already done when EDIT was pressed — the edit itself revokes everything after
  // the edited step (`demoteForActiveStep`), so that set is the only record of what was walked.
  const reviewReturnRef = useRef<{ reviewId: string; walked: ReadonlySet<string> } | null>(null);
  // Back on Review, however they got there, the round trip is over.
  useEffect(() => {
    if (reviewReturnRef.current?.reviewId === activeStepId) reviewReturnRef.current = null;
  }, [activeStepId]);
  // Bumped when Next is pressed on an unfinished step; see ShowErrorsContext. Kept WITH the step it
  // was raised on, so the step the advisor arrives on reads 0 from its very first render. A reset in
  // an effect came one render late: the new step's fields mounted, saw the old count and painted
  // every required error on arrival (QA observation 1 / RDSP-UI-03).
  const [errorsRaised, setErrorsRaised] = useState<{ stepId: string | null; tick: number }>({
    stepId: null,
    tick: 0,
  });
  const showErrorsTick = errorsRaised.stepId === activeStepId ? errorsRaised.tick : 0;
  // The "CLIENT:" badge. Reported upward rather than drawn here: the badge sits beside the form
  // title, which belongs to the page that mounts this shell.
  const primaryName = useMemo(
    () => primaryClientName(template, values, advancedSteps),
    [template, values, advancedSteps],
  );
  useEffect(() => {
    onPrimaryClientNameChange?.(primaryName);
  }, [onPrimaryClientNameChange, primaryName]);

  // Validate that `activeStepId` still points at a step that exists AND is
  // currently visible. If the visibility flipped (or the saved id is stale),
  // jump state forward to the first visible step in document order so the
  // persisted draft, rail highlight, and nav arithmetic all agree. Without
  // this, state pins to a hidden step and a future re-show snaps the user
  // back unexpectedly.
  useEffect(() => {
    if (allSteps.length === 0) return;
    // Preview mode lets a reviewer LAND on (and stay on) a conditionally-hidden
    // step reached via the per-step "Preview" deep link (?stepId=…), so they can
    // inspect it without walking the whole flow. There we only require the step
    // to EXIST — a hidden-but-real step stays active. Advisor mode still requires
    // visibility, so the applicant is jumped off any step conditions have hidden.
    // A genuinely stale id (matches no step) falls through to the first-visible
    // jump in BOTH modes. Note this only governs the active step; Back/Next still
    // skip hidden steps in every mode (see prevStep/nextStep below).
    // Audience eligibility is checked even in preview, unlike conditions: the deep link
    // exists so a reviewer can inspect a step their answers switched off, which is a
    // different thing from a step this AUDIENCE must never see. In practice preview is
    // always 'internal' so the term is inert there — it is spelled out so a future
    // audience cannot be deep-linked around.
    const stillValid =
      activeStepId !== null &&
      allSteps.some(
        (s) =>
          s.id === activeStepId &&
          isStepEligibleForAudience(s, audience) &&
          (mode === 'preview' || evaluateConditions(s.conditions, s.conditionCombinator, values)),
      );
    if (stillValid) return;
    // The fallback has to honour the audience too, or a customer bounced off a hidden
    // step could be jumped straight onto another one.
    const firstVisible =
      allSteps.find(
        (s) =>
          isStepEligibleForAudience(s, audience) &&
          evaluateConditions(s.conditions, s.conditionCombinator, values),
      )?.id ??
      allSteps.find((s) => isStepEligibleForAudience(s, audience))?.id ??
      null;
    if (firstVisible !== activeStepId) setActiveStepId(firstVisible);
  }, [allSteps, values, activeStepId, mode, audience]);

  // Retire the Next spinner the moment the step swap has been rendered. Layout, not passive:
  // useEffect runs AFTER paint, which would flash the incoming step under a still-spinning
  // button for a frame. Clearing it inside `advanceToNext` instead would be worse — that
  // setState batches with `setActiveStepId`, so the spinner would come down for exactly the
  // render it exists to cover. Aborted advances (failed save, failed document delivery) never
  // change the step, so they clear the flag inline themselves.
  useLayoutEffect(() => {
    setAdvancing(false);
  }, [activeStepId]);

  // Put the advisor back at the TOP whenever the step changes.
  //
  // Next/Back sit at the BOTTOM of the form, so without this the incoming step
  // renders with the viewport still parked where the old step's buttons were:
  // on a long step you land mid-form, or past the end of a short one, with no
  // visual cue that the page changed at all.
  //
  // BOTH axes are reset on purpose. `.form-shell__main` is its own scroll
  // container (`overflow-y: auto`), but the shell is taller than the viewport
  // on most steps, so the WINDOW scrolls too — resetting only one leaves the
  // advisor part-way down whichever one was actually scrolled.
  //
  // Layout effect, not passive: this runs before paint, so the new step is
  // never briefly painted at the old offset and then yanked upward.
  // Instant, not smooth — the content has already swapped, so animating a
  // scroll through content the advisor never asked to see is noise (and would
  // need a `prefers-reduced-motion` opt-out to boot).
  //
  // Both calls are OPTIONAL-invoked. jsdom implements neither `Element.scrollTo`
  // nor a real `window.scrollTo`, so calling the element one unguarded throws
  // "scrollTo is not a function" on every step change — which took out 43
  // unrelated FormShell tests the first time this shipped. Guarding costs
  // nothing in a browser and keeps the shell renderable anywhere.
  useLayoutEffect(() => {
    mainRef.current?.scrollTo?.({ top: 0, behavior: 'auto' });
    window.scrollTo?.({ top: 0, behavior: 'auto' });
  }, [activeStepId]);

  // Memoize the per-step visibility map ONCE per `values` change. A pure
  // function of `(allSteps, values)`, so re-evaluating on every render (the
  // pre-memo behaviour) re-walked the entire workflow on every keystroke
  // — measurable lag on 40+ field forms. The map is cheap to lookup and
  // every downstream computation (visibleSteps / activeStep / prev / next
  // / rail-per-step-isComplete) can share it.
  const stepVisibility = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const s of allSteps) {
      map.set(
        s.id,
        // Audience first: a step this runtime must not show is hidden whatever the
        // answers say, and skipping the condition walk for it is free. This is the ONE
        // place the audience rule belongs — every downstream computation (isVisible,
        // visibleSteps, railSteps, activeStep, prevStep/nextStep, stepCompletion,
        // deriveResumePosition) reads this map, so they all inherit it for free.
        isStepEligibleForAudience(s, audience) &&
          evaluateConditions(s.conditions, s.conditionCombinator, values),
      );
    }
    return map;
  }, [allSteps, values, audience]);
  /**
   * "Disable if" at STEP level — every section and field on the step renders inert.
   *
   * Deliberately NOT folded into `stepVisibility` above: that map means HIDDEN, and a hidden
   * step drops out of `visibleSteps` / `prevStep` / `nextStep` / the rail, and has its answers
   * stripped on submit. A disabled step must stay visible, stay listed and clickable in the
   * rail, keep its place in the Next/Back chain, and keep shipping its answers — so it is a
   * separate map that feeds exactly one thing: the `readOnly` prop handed to StepContent.
   */
  const stepDisabled = useMemo(
    () => new Map(allSteps.map((s) => [s.id, isDisabledByRule(s, values)])),
    [allSteps, values],
  );
  const isVisible = useCallback(
    (s: WorkflowStep) => stepVisibility.get(s.id) ?? true,
    [stepVisibility],
  );
  const visibleSteps = useMemo(() => allSteps.filter(isVisible), [allSteps, isVisible]);
  // Advisor-only: steps the applicant has not reached yet. A step enters `reachedSteps` only by
  // being stood on or Next'd past, and Next is gated on that step's required fields — so
  // "already reached" is exactly "cannot skip ahead past unfilled required fields" (per Ugur
  // 2026-08-05). Preview navigates freely; a reviewer is not filling the form in.
  //
  // Read `reachedSteps`, NOT `advancedSteps`: the latter gains a step only on the way OUT of it,
  // so the furthest step the advisor has got to is absent from it and would re-lock the instant
  // they pressed Back (BMO #314). Returning to a step already visited skips nothing — it is
  // where Next would have put them anyway.
  //
  // A LOCKED application navigates freely too, for the same reason: this gate exists to stop an
  // advisor skipping past required fields they still owe, and there is nothing left to owe on an
  // application BMO has taken over. Without this it would mount with an empty set and lock the
  // whole rail, leaving the advisor unable to read their own submitted work.
  const railLocked = useMemo(() => {
    // Standing on ThankYou locks the rail COMPLETELY, in preview too. By this point every
    // earlier step is in `advancedSteps`, so the ordinary rule below would leave them all
    // clickable and let the advisor wander back into an application BMO has already been
    // told is Submitted. The card's own CTAs are the only ways out, which is the whole
    // point of a terminal step. Preview is included deliberately: an originator checking
    // the flow should meet the same dead end the advisor gets.
    //
    // Belt and braces since 2026-11-02: the rail is no longer RENDERED on ThankYou at all
    // (see the render below), so nothing currently consults this branch. Kept because it
    // states the rule — the terminal step has no navigation — at the layer that decides
    // what is clickable, so re-showing the rail here (or mounting the step list anywhere
    // else) cannot quietly reopen the path back into a submitted application.
    if (allSteps.find((s) => s.id === activeStepId)?.type === 'ThankYou') {
      return new Set(allSteps.filter((s) => s.id !== activeStepId).map((s) => s.id));
    }
    if (mode === 'preview' || locked) return new Set<string>();
    return new Set(
      allSteps.filter((s) => !reachedSteps.has(s.id) && s.id !== activeStepId).map((s) => s.id),
    );
  }, [mode, locked, allSteps, reachedSteps, activeStepId]);

  // Shared visibility gate used by BOTH the on-blur uniqueness check and the
  // workflow-wide `findAllUniquenessConflicts` walk — binding this component's
  // memos to the pure rule. See {@link fieldVisibleForUniqueness} for why both
  // views must agree and how a repeatable instance is resolved.
  const isFieldVisibleForUniqueness = useCallback(
    (field: WorkflowField, stepId: string, instance = 0): boolean =>
      fieldVisibleForUniqueness(
        { steps: allSteps, stepVisibility, values, allSections },
        field,
        stepId,
        instance,
      ),
    [allSteps, stepVisibility, values, allSections],
  );
  // Cross-field uniqueness lookup — passed down to every ShortText's on-blur
  // check via FormRenderer. Returns the peer field's advisor-facing label
  // (in the current language) when the value collides, so the error message
  // can name the culprit ("This email is already used by Beneficiary").
  const findUniquenessConflictMessage = useCallback(
    (field: WorkflowField, next: FieldValue, ownValueKey?: string): string | null => {
      // Pass `next` as the override so the walker sees what the advisor
      // is about to commit without allocating a full values-map spread —
      // repeatable-section fields live under qualified keys the caller
      // doesn't know here, and the old spread wrote to the bare id.
      // `ownValueKey` is the current instance's key so the walker skips
      // the right instance (without it, editing a 2nd+ instance flags the
      // field against its own value — the repeatable-section false positive).
      const hit = findUniquenessConflict(
        field,
        template,
        values,
        (peerField, stepId, _sectionIndex, peerInstance) =>
          isFieldVisibleForUniqueness(peerField, stepId, peerInstance),
        next,
        ownValueKey,
      );
      if (hit === null) return null;
      const peer =
        (language === 'fr' && hit.peer.labelFr ? hit.peer.labelFr : hit.peer.labelEn) ||
        hit.peer.labelEn;
      // Name the STEP too. Peer labels repeat across a workflow (RDSP has six
      // "Mobile Phone" fields in one group), so the label alone produced
      // "already used by 'Mobile Phone'" while the advisor stood on a field
      // called Mobile Phone — self-accusing and unactionable. Fall back to the
      // bare message only if the step somehow has no title in either locale.
      //
      // Named the way the RAIL names it: this message sends the advisor off to go and find
      // that step, and the rail is where they will look for it.
      const step = stepNavTitle(hit.step, language);
      // The field's own wording wins ("Email address must be unique across all signatories.");
      // it may name {{peer}} / {{step}} like the generic copy.
      if (field.validationMessages?.some((m) => m.key === 'uniqueness'))
        return resolveValidationMessage(field, 'uniqueness', { peer, step }, language, t);
      return step
        ? t('formRenderer.validation.uniquenessOnStep', { peer, step })
        : t('formRenderer.validation.uniqueness', { peer });
    },
    [values, template, language, isFieldVisibleForUniqueness, t],
  );
  // Active-step fallback: if the currently active step has been hidden by a
  // recent value change, jump forward to the first visible step. (If no step
  // is visible at all, we still render the first step so the form doesn't
  // blank out — the rail will then be empty.) The `?? null` on the
  // last fallback is for the zero-step workflow path; the render below
  // short-circuits in that case.
  // In preview mode a hidden active step (landed on via ?stepId=) is honored so
  // the reviewer can inspect it; advisor mode discards it and falls back to the
  // first visible step. The rail omits hidden steps, so once the reviewer leaves
  // that step there is no way back to it except the deep link — which is the
  // point: the rail lists the flow, the deep link is an inspection escape hatch.
  const activeStep =
    allSteps.find((s) => s.id === activeStepId && (mode === 'preview' || isVisible(s))) ??
    visibleSteps[0] ??
    allSteps[0];

  // Live representative codes, re-read each time the advisor LANDS on a step that carries a
  // rep-code dropdown. The session's codes are otherwise a snapshot from ISAM login and stay
  // that way for up to the absolute session cap (POST /auth/refresh rotates tokens but never
  // re-reads the GAMX profile), so a code granted or withdrawn mid-session would be invisible
  // both to the dropdown's options and to `decideAdvisorPrefill`'s "is this still one of THEIR
  // codes?" test.
  //
  // Nothing is decided here. `applySessionRepCodes` publishes a new session object, which
  // re-runs the prefill pass above — and THAT is what empties a pick the advisor no longer
  // holds, auto-selects a list that has collapsed to a single code, and leaves a still-valid
  // pick alone. One policy, in one place.
  //
  // Keyed on `activeStep` so it fires per navigation, including a step the advisor walks back
  // onto. The gates mirror the prefill pass exactly: preview is driven by an originator session
  // with no GAMX profile at all (it renders PREVIEW_ADVISOR_REP_CODES instead), and a filed
  // application is a record of what went to BMO, not a form to keep current — `isAdvisorWritable`
  // is the same vocabulary `decideAdvisorPrefill` reads, not a second list to keep in step.
  //
  // The dependency is `sessionReady`, NOT `user`: a successful refresh publishes a new session
  // object, and depending on the object itself would make this effect re-fire on its own result
  // — a second GAMX call for every visit that actually changed something. What it waits for is
  // only that /me has landed at all.
  const sessionReady = user !== null;
  useEffect(() => {
    // `!== 'advisor'` is deliberately right here rather than a narrowing oversight:
    // preview has no GAMX profile and a PUBLIC customer has no session at all, so both
    // must skip the refresh. Do not "fix" this to use rendererMode - that maps public
    // onto 'advisor' and would fire a rep-code lookup for an anonymous caller.
    if (mode !== 'advisor' || !sessionReady) return;
    if (!isAdvisorWritable(status)) return;
    if (!activeStep || !stepNeedsRepCodes(activeStep)) return;
    let alive = true;
    void refreshAdvisorRepCodes().then((codes) => {
      // null is "the refresh didn't land" — the endpoint answers with the stored codes rather
      // than an error when GAMX itself is unhelpful. Either way the advisor keeps the options
      // they had; a failed lookup must never empty a Required dropdown.
      if (!alive || codes === null) return;
      applySessionRepCodes(codes);
    });
    return () => {
      alive = false;
    };
  }, [activeStep, mode, status, sessionReady]);

  // Prev/next-step targets — nearest VISIBLE step on either side. Memoized
  // because each computation walks `allSteps` and was happening on every
  // keystroke.
  const { prevStep, nextStep } = useMemo(() => {
    if (!activeStep) return { prevStep: null, nextStep: null };
    const activeIndex = allSteps.findIndex((s) => s.id === activeStep.id);
    const prev =
      activeIndex > 0
        ? ([...allSteps.slice(0, activeIndex)].reverse().find(isVisible) ?? null)
        : null;
    const next =
      activeIndex >= 0 ? (allSteps.slice(activeIndex + 1).find(isVisible) ?? null) : null;
    return { prevStep: prev, nextStep: next };
  }, [allSteps, activeStep, isVisible]);

  /**
   * The step carrying the flow's FINAL action — Submit for an advisor, "Download all" for
   * preview and the public customer. Null when this flow has no final action at all, in which
   * case every step keeps its ordinary Next.
   *
   * Two rules, because the two runtimes no longer agree on where the flow ends:
   *
   *   - Preview and the public customer still walk the generation step, and their action is
   *     that step's own content — the bulk download of what it lists. It has nowhere else it
   *     could live, so the match stays on the type, exactly as it always was.
   *   - The advisor's generation step is folded away (see `foldsPdfStep`), so Submit lands on
   *     whatever is now LAST: Document Upload in every real workflow, but e-Signature, Review
   *     or a plain form step in one that configures no documents — or whose document step is
   *     conditioned out by the answers. A type match would leave those with no way to submit.
   *
   * ThankYou is excluded from the search, not merely "not last": it is the step this action
   * LEADS to. And the advisor rule is gated on `onSubmit` for the same "absence is the gate"
   * reason that prop itself is: with no handler there is no final action to offer, so the last
   * step must keep its Next rather than sprout a button that goes nowhere.
   */
  const submitStep = useMemo(() => {
    if (!foldsPdfStep) return allSteps.find((s) => s.type === 'PdfGeneration') ?? null;
    if (!onSubmit) return null;
    const working = allSteps.filter((s) => s.type !== 'ThankYou' && isVisible(s));
    return working[working.length - 1] ?? null;
  }, [allSteps, isVisible, foldsPdfStep, onSubmit]);

  // Three gates on Next:
  //   1. Blocking-value: any visible field on the active step whose current
  //      value matches its originator-authored `blockingValues` gate (e.g.
  //      "Resident = no" pins Next forever). Wins over everything else —
  //      filling more fields can't unblock a dead-end answer.
  //   2. Missing required: visible required fields that are still empty.
  //   3. No target: no visible next step in document order.
  const blockingField = useMemo(
    () => (activeStep ? findBlockingField(activeStep, values) : null),
    [activeStep, values],
  );
  // Orthogonal to the three Next gates above and NOT a fourth one: a triggered
  // redirect doesn't disable anything, it offers a way OFF this form. (An
  // originator who also wants Next pinned while the answer stands pairs this
  // with a blocking value — the two compose.) Scoped to the active step because
  // that is where the advisor just clicked; an answer on a step they have
  // navigated away from must not yank them somewhere on arrival.
  const activeRedirect = useMemo(
    () => (activeStep ? findOptionRedirect(activeStep, values) : null),
    [activeStep, values],
  );
  // ---- Form branching -------------------------------------------------------------
  // The inline sibling of the redirect above: instead of offering a way OFF this form, it
  // replaces the rest of THIS one. Resolved across the WHOLE step list rather than the
  // active step, because once the swap has happened the triggering step sits behind the
  // advisor and the branch has to keep resolving from there — otherwise the first
  // navigation forward would read "no branch" and collapse the form back to the base.
  const activeBranchTargetId = useMemo(
    () =>
      onFormBranchChange
        ? (resolveActiveBranch(allSteps, values)?.branch.targetTemplateId ?? null)
        : null,
    [onFormBranchChange, allSteps, values],
  );
  // Answers parked per branch, so swapping B → C → B does not ask B's questions twice.
  // A ref, not state: nothing renders from it, and a re-render between the swap being
  // detected and the host's new template arriving must not drop a park in progress.
  const branchStashRef = useRef<Map<string, Record<string, FieldValue>>>(new Map());
  const uploadStashRef = useRef<Map<string, UploadedFilesStore>>(new Map());
  const appliedBranchRef = useRef<Props['formBranch']>(null);
  const requestedBranchRef = useRef<string | null>(null);
  // Tell the host. Guarded on the ref rather than on a dependency comparison so a values
  // edit that leaves the branch unchanged — which is most of them — costs nothing.
  useEffect(() => {
    if (!onFormBranchChange) return;
    if (requestedBranchRef.current === activeBranchTargetId) return;
    requestedBranchRef.current = activeBranchTargetId;
    onFormBranchChange(activeBranchTargetId);
  }, [activeBranchTargetId, onFormBranchChange]);
  // The host has answered with a newly composed template. Park the outgoing branch's
  // answers and uploads, then restore the incoming one's — or, on a first visit, leave it
  // empty so the new form opens unanswered, which is the whole point of keeping the two
  // sets apart.
  useEffect(() => {
    const prev = appliedBranchRef.current ?? null;
    const next = formBranch ?? null;
    if ((prev?.targetTemplateId ?? null) === (next?.targetTemplateId ?? null)) return;
    appliedBranchRef.current = next;

    setValues((current) => {
      let out = current;
      if (prev) {
        const parked: Record<string, FieldValue> = {};
        out = {};
        for (const [key, value] of Object.entries(current)) {
          if (ownsValueKey(prev.owned, key)) parked[key] = value;
          else out[key] = value;
        }
        branchStashRef.current.set(prev.targetTemplateId, parked);
      }
      if (next) {
        const restored = branchStashRef.current.get(next.targetTemplateId);
        if (restored) out = { ...out, ...restored };
      }
      // "Selected by default" answers belong to the incoming form too, and it has never
      // been shown — the same moment-of-first-sight rule a fresh application gets at
      // mount. Never overwrites an answer, and returns `out` untouched when it writes
      // nothing, so a restored branch keeps exactly what was parked.
      return applyDefaultOptions(template, out, { clientLanguage: pdfLanguage });
    });

    setUploadedFiles((current) => {
      if (prev) uploadStashRef.current.set(prev.targetTemplateId, current);
      if (next) return uploadStashRef.current.get(next.targetTemplateId) ?? EMPTY_UPLOADS;
      return EMPTY_UPLOADS;
    });

    // Every cached fill was built against the outgoing form's PDF templates. Keeping them
    // would show the advisor a document from a form they are no longer filling.
    pdfCacheRef.current.forEach(({ url }) => URL.revokeObjectURL(url));
    pdfCacheRef.current.clear();
  }, [formBranch, template, pdfLanguage]);
  // Precompute uniqueness-group offenders once per (template, values,
  // stepVisibility). `findAllUniquenessConflicts` respects field/section
  // visibility so a hidden peer's value doesn't count against a visible
  // peer's — otherwise a hidden AH2 would block advance the moment AH1
  // + AH2 shared a placeholder value.
  const uniquenessOffenders = useMemo(
    () =>
      findAllUniquenessConflicts(template, values, (field, stepId, _sectionIndex, instance) =>
        isFieldVisibleForUniqueness(field, stepId, instance),
      ),
    [template, values, isFieldVisibleForUniqueness],
  );
  // ZeroBounce for every ordinary email input, asked on blur. One store for the whole
  // shell, read BOTH by the field tree (through the context below, for the red text) and
  // by the completion gates here (for the held Next) — the same single-source arrangement
  // `failsFormatRule` already gives the local rules, so the message and the button can
  // never disagree. Null when the capability is off; the gates then behave exactly as
  // they did before this existed.
  const emailVerify = useEmailVerification(emailVerification !== false);
  const emailLookup = emailVerify?.lookup;
  // Everything holding Next back on the active step, each tagged missing-vs-invalid so the
  // summary can say "fill" or "correct" — see `blockingFieldIssues`.
  const fieldIssues = useMemo(
    () =>
      activeStep
        ? blockingFieldIssues(
            activeStep,
            values,
            language,
            uniquenessOffenders,
            uploadedFiles,
            user,
            allSections,
            audience,
            emailLookup,
          )
        : [],
    [
      activeStep,
      values,
      language,
      uniquenessOffenders,
      uploadedFiles,
      user,
      allSections,
      audience,
      emailLookup,
    ],
  );
  // Per-step completion map — built once per (allSteps, values, advancedSteps,
  // stepVisibility) change. Replaces the per-StepItem `isStepComplete(step,
  // values)` call which used to walk the same tree N times per keystroke.
  const stepCompletion = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const s of allSteps) {
      const visible = stepVisibility.get(s.id) ?? true;
      const advanced = advancedSteps.has(s.id);
      map.set(
        s.id,
        visible &&
          advanced &&
          // A step with nothing to fill in is done once the advisor has moved past it (QA D-13:
          // Review and eSignature never showed their tick). Rail-only on purpose — resume reads
          // `isStepComplete` directly, and must keep landing on Review for a finished application.
          (PASSIVE_STEP_TYPES.has(s.type) ||
            isStepComplete(
              s,
              values,
              uniquenessOffenders,
              uploadedFiles,
              user,
              allSections,
              audience,
              emailLookup,
            )),
      );
    }
    return map;
  }, [
    allSteps,
    values,
    advancedSteps,
    stepVisibility,
    uniquenessOffenders,
    uploadedFiles,
    user,
    allSections,
    audience,
    emailLookup,
  ]);

  // SFA-8 — open a resumed application where the advisor's attention is needed,
  // and unlock everything behind it so they can edit what they already entered.
  //
  // Runs once, on mount. `AdvisorApplicationPage` holds the shell back until the
  // resume read has settled, so the answers and restored documents this reads are
  // already in place on the first render.
  //
  // `isStepComplete` directly rather than the `stepCompletion` map above: that map
  // ANDs in `advancedSteps`, which is empty at mount, so every step would read as
  // incomplete and the advisor would always land on step one.
  const resumePositionApplied = useRef(false);
  useEffect(() => {
    if (!isResumedApplication || resumePositionApplied.current) return;
    // An explicit deep link already chose the step; don't overrule it. Every step before it
    // counts as seen, for the same reason as the prior steps below.
    if (initialStepId || allSteps.length === 0) {
      resumePositionApplied.current = true;
      const linked = allSteps.findIndex((s) => s.id === initialStepId);
      for (const s of allSteps.slice(0, Math.max(linked, 0))) shownStepsRef.current.add(s.id);
      return;
    }
    resumePositionApplied.current = true;

    const { stepId, priorStepIds } = deriveResumePosition(
      allSteps,
      (step) => stepVisibility.get(step.id) ?? true,
      // No email lookup here, and it would be dishonest to pass one: this runs once at mount,
      // where nothing has been blurred and the store is necessarily empty. A resumed
      // application's addresses get verified when the advisor reaches and leaves them, which
      // is also the only point at which they could act on a verdict.
      (step) =>
        isStepComplete(
          step,
          values,
          uniquenessOffenders,
          uploadedFiles,
          user,
          allSections,
          audience,
        ),
    );
    // The advisor walked these in an earlier session. A slot left empty there — a default they
    // cleared — must stay empty now, so they join the shown set before the default pass below
    // reads it (it runs after this effect in the same commit).
    for (const id of priorStepIds) shownStepsRef.current.add(id);
    if (!stepId) return;
    shownStepsRef.current.add(stepId);

    // The resume step joins the advanced set too — the advisor HAS reached it, so
    // stepping back to an earlier step must not lock it behind them.
    setAdvancedSteps((prev) => new Set([...prev, ...priorStepIds, stepId]));
    setActiveStepId(stepId);
    // Mount-only by the ref guard; the deps are what the derivation reads.
  }, [
    isResumedApplication,
    initialStepId,
    allSteps,
    stepVisibility,
    values,
    uniquenessOffenders,
    uploadedFiles,
    user,
    allSections,
    // Inert in practice — `audience` is derived from `mode`, which never changes for the
    // life of a mount, and the ref guard makes this effect mount-only anyway. Listed
    // because the completeness probe above genuinely reads it.
    audience,
  ]);
  // "Selected by default" (QA D-17 / D-42) after mount. The initial state already filled a
  // fresh application; this pass covers what can change afterwards — a step not yet reached
  // (every one of them, for a resumed application) and the client's language, which can make a
  // scoped default available or clear it. Gated exactly like the cross-step prefill above:
  //   - a step already SHOWN is never touched, so a default the advisor cleared stays cleared;
  //   - a slot the advisor EDITED is theirs;
  //   - a slot RESTORED with an answer from a save is never written over.
  // Only ever fills an EMPTY slot. Declared after the resume effect on purpose: on a resumed
  // mount that effect marks the steps walked in an earlier session as shown first.
  useEffect(() => {
    if (isResumedApplication && !resumePositionApplied.current) return;
    setValues((prev) =>
      applyDefaultOptions(template, prev, {
        clientLanguage: pdfLanguage,
        skipStep: (id) => shownStepsRef.current.has(id),
        skipKey: (key) =>
          editedFieldsRef.current.has(key) || isNonEmptyValue(restoredValuesRef.current[key]),
      }),
    );
  }, [isResumedApplication, template, pdfLanguage, values, editedTick]);
  // The step an edit should be attributed to. A ref rather than the `activeStep` binding because
  // some writes land asynchronously — an upload promise or a debounced control can resolve after
  // the advisor has already rail-clicked away, and demoting against the step they LEFT would
  // charge them for an edit they made somewhere else.
  const activeStepRef = useRef<WorkflowStep | null>(null);
  useEffect(() => {
    activeStepRef.current = activeStep ?? null;
  }, [activeStep]);
  // `blockContinueRestart` (QA RIF TC-06-03, legacy RIF): closing a blocking alert starts over — the
  // step's answers go, and the advisor is back on the Welcome step.
  const restartFromBlock = () => {
    const step = activeStepRef.current ?? activeStep;
    if (!step) return;
    const ids = new Set<string>();
    for (const section of step.sections ?? [])
      for (const row of section.rows)
        for (const col of row.columns) if (col.field) ids.add(col.field.id);
    const sectionIds = new Set((step.sections ?? []).map((s) => s.id));
    setValues((prev) => {
      const next: Record<string, FieldValue> = {};
      for (const [k, v] of Object.entries(prev)) {
        const inst = /^inst:([^:]+):/.exec(k);
        if (ids.has(k) || (inst && sectionIds.has(inst[1]!))) continue;
        next[k] = v;
      }
      return next;
    });
    const welcome = allSteps.find((s) => s.type === 'Welcome') ?? allSteps[0];
    if (welcome) setActiveStepId(welcome.id);
  };

  if (allSteps.length === 0 || !activeStep) {
    return <div style={{ padding: 24, color: '#475569' }}>{t('formRenderer.noSteps')}</div>;
  }
  // Next is busy while its own advance is in flight, and also while Save and exit is saving —
  // two concurrent snapshots of different values could land in the wrong order.
  const nextBusy = saving || advancing;
  // Missing or invalid answers do NOT disable Next (QA D-36). Pressing it names them instead —
  // see `revealMissing`. The gates left here each already explain themselves where the advisor
  // is looking: a disqualifying answer shows its own message, a step blocker its tooltip.
  // A blocking answer no longer greys Next out (QA RIF C7: "Next … enabled at all times"): pressing
  // it names the answer instead, under the field and in the summary — see revealBlockingAnswer.
  const nextDisabled = !nextStep || stepBlocker !== null || nextBusy;

  /**
   * Pushes the current snapshot to BMO and reports whether it landed.
   *
   * Resolves `true` when there is no `onSave` handler at all — that is preview, where there is
   * nothing to persist and navigation must not be gated on a call that will never happen.
   *
   * A rejection is surfaced and returns `false` so the caller stays put. The one exception is
   * session expiry, which runs its own warning-modal / redirect flow (ADR-0022); showing a
   * "save failed" toast over that would tell the advisor their work was lost when the real
   * answer is "sign in again".
   *
   * <h3>No session ping here — `applicationsApi` owns it</h3>
   * The save path deliberately does NOT call `notifySessionActivity()`. `api/applications.ts`
   * awaits that ping itself before the request leaves, which both refreshes the ISAM token in
   * time and keeps it to one grant. Pinging here as well produced two concurrent
   * `POST /auth/refresh` per Next; ISAM rotates the refresh token on use, so the second was
   * refused with `invalid_grant`, the BFF ended the session, and the advisor was logged out by
   * the click that was trying to save (UAT, 2026-08-26).
   */
  /**
   * The signer roster + uploaded documents as of RIGHT NOW.
   *
   * Resolved fresh on every call rather than held in state: the roster is a function of the
   * current answers (a conditional signer appears or vanishes as the advisor edits), and a stale
   * copy would send BMO a different set of people than the summary table shows.
   *
   * Shared with the dev-only eSignInfo probe so the payload it displays and the payload the next
   * save sends are resolved by one call, not two lookalikes.
   */
  const currentESignSubmission = (): ESignSubmission =>
    buildESignSubmission({
      template,
      values,
      uploadedFiles,
      entryMeta: collectEntryMeta(template),
      advisor: {
        fullName: user?.name ?? user?.preferred_username ?? null,
        firstName: user?.firstName ?? null,
        lastName: user?.lastName ?? null,
        email: user?.email ?? null,
        phone: user?.phoneNumber ?? null,
      },
      // The PDF language, not the advisor's UI locale — it is the one that
      // decides which placements exist for this application, so it is the one
      // that decides whether a signer has anything left to sign.
      language: pdfLanguage,
    });

  const persistSnapshot = async (
    statusOverride?: string,
    // The press instant, passed in rather than read here — only `persistAndDeliver` has one,
    // because only it is followed by a deliver call that has to compose the same §14.1 names.
    deliveryTimestamp?: string,
  ): Promise<boolean> => {
    if (!onSave) return true;
    setSaving(true);
    try {
      // Status is read from the ref, not from render state, so a demotion that landed between
      // the click and this line still travels with the values it describes. `statusOverride` is
      // how Next sends the rung it is about to earn in the SAME call as the answers.
      await onSave(
        buildApplicationSnapshot(payloadTemplate ?? template, values, pdfLanguage),
        statusOverride ?? bmoStatusRef.current,
        currentESignSubmission(),
        deliveryTimestamp,
      );
      // The save landed, so the bottom rung is spent: APPLICATION_INITIATED describes the call
      // that CREATES the application and nothing after it. Promoting here rather than in
      // `advanceToNext` covers Save-and-exit too, and it is deliberately keyed off the save
      // succeeding rather than off passing a step — the first save usually leaves a Welcome or
      // DynamicForm step, and neither earns a milestone. A no-op above the bottom rung.
      commitStatus(promoteFromInitiated);
      return true;
    } catch (err) {
      if (!(err instanceof ApplicationSessionExpiredError)) {
        void message.error(t('formRenderer.saveFailed'));
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  /**
   * The advisor changed something on the step they're standing on. Two consequences, and they
   * have to move together or the rail and BMO end up telling the advisor different stories:
   *
   *   1. The status drops to the rung their change invalidated (see ./bmoStatus) — it rides
   *      along with the next save rather than firing one, since there is no autosave and a save
   *      per keystroke would be an autosave by stealth.
   *   2. Every step AFTER this one loses its "advanced past" flag, so the rail re-locks ahead of
   *      the edit. An advisor who walks to e-signature, comes back to the first dynamic step and
   *      changes an answer has to Next through the intervening steps again — they cannot
   *      rail-click straight back to where they were, because the milestones in between are
   *      exactly what their edit just invalidated.
   *
   * Strictly AFTER, never at-or-before: the edited step keeps its own flag and so does
   * everything earlier, which is what stops the advisor being locked out from behind. They stand
   * on an unlocked step, press Next, and walk forward one click per step.
   *
   * The revocation is keyed off the EDIT, not off whether the status numerically moved.
   * `demoteStatus` is a no-op when the status already sits at the ceiling (editing a dynamic
   * field while at IN-PROGRESS), but the steps ahead are invalidated all the same.
   */
  const demoteForActiveStep = () => {
    const step = activeStepRef.current ?? activeStep;
    const ceiling = ceilingForStep(step.type);
    // ApplicationReview / PdfGeneration are recap surfaces with no change handler wired at all,
    // so a demotion from one means a wiring bug — and demoting on a bug is worse than not.
    if (!ceiling) return;
    commitStatus((prev) => demoteStatus(prev, ceiling));
    const idx = allSteps.findIndex((s) => s.id === step.id);
    if (idx < 0) return;
    const forwardIds = allSteps.slice(idx + 1).map((s) => s.id);
    // Identity-preserving when nothing changed — `railLocked`, `reachedSteps` and
    // `stepCompletion` are memos keyed on these sets, and a fresh Set on every keystroke would
    // re-walk the whole workflow.
    const withoutForward = (prev: Set<string>) => {
      let next: Set<string> | null = null;
      for (const id of forwardIds) {
        if (!prev.has(id)) continue;
        (next ??= new Set(prev)).delete(id);
      }
      return next ?? prev;
    };
    setAdvancedSteps(withoutForward);
    // The visited set is revoked in lockstep, or the rail would still offer a one-click jump to
    // a step the edit just invalidated — which is the whole behaviour this demotion exists to
    // prevent. The edited step itself and everything before it are untouched in BOTH sets, so
    // the advisor is still never locked out from behind.
    setVisitedSteps(withoutForward);
  };

  /**
   * Lands on the workflow's terminal confirmation card.
   *
   * Shared by the two ways a flow can finish, which are genuinely different endings: an
   * advisor's Submit (BMO has the application) and a public customer's "Download all"
   * (nothing was sent anywhere and the files are in their downloads folder). One
   * implementation because the NAVIGATION is identical either way — mark the step being
   * left as advanced so the rail paints its check, then move — and two copies of that
   * would drift on the next rail change.
   *
   * No-op on a workflow with no ThankYou step, which is possible: it is a
   * platform-managed singleton the originator can leave off.
   */
  const goToThankYou = () => {
    const thankYou = allSteps.find((s) => s.type === 'ThankYou');
    if (!thankYou || !activeStep) return;
    setAdvancedSteps((prev) => {
      if (prev.has(activeStep.id)) return prev;
      const next = new Set(prev);
      next.add(activeStep.id);
      return next;
    });
    setActiveStepId(thankYou.id);
  };

  // Shared by the nav row's Next button and the Welcome step's CONTINUE:
  // save the whole application, then mark the step we're leaving as "advanced past" so the
  // rail can paint its blue check, then move to the nearest visible next step.
  //
  // The save gates the advance deliberately: values live in React memory only, so moving on
  // from a failed save is how work goes missing.
  // Next on a step that still has something missing or wrong: every field says what it needs,
  // a summary names them, and the first one takes focus. The advance does not happen.
  const revealMissing = () => {
    setErrorsRaised((prev) => ({
      stepId: activeStepId,
      tick: prev.stepId === activeStepId ? prev.tick + 1 : 1,
    }));
    void message.warning(nextBlockedMessage(fieldIssues, t));
    // After the fields have re-rendered with their errors. A timeout, not rAF: a background tab
    // never fires rAF.
    setTimeout(() => {
      mainRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
    }, 0);
  };

  /**
   * Next on a step whose answer is a dead end (a field's `blockingValues`): the advance does not
   * happen, the field shows why (its authored block-continue message, or a generic one) and takes
   * focus, and the same words appear in the summary toast.
   */
  const revealBlockingAnswer = (field: WorkflowField) => {
    setErrorsRaised((prev) => ({
      stepId: activeStepId,
      tick: prev.stepId === activeStepId ? prev.tick + 1 : 1,
    }));
    void message.warning(blockingAnswerMessage(field, language, t));
    setTimeout(() => {
      mainRef.current
        ?.querySelector<HTMLElement>(
          `[data-blocked-field="${field.id}"] input, [data-blocked-field="${field.id}"]`,
        )
        ?.focus();
    }, 0);
  };

  /**
   * Save the whole application, then — when the step being left is an AdditionalDocuments step
   * — hand its uploads to BMO and block until delivery confirms (ADR-0026).
   *
   * Spec §14 fixes that order and it is not an implementation detail: "BMO acts on the submitted
   * documents we SFTP to them, not on the JSON save data we send via the API", so the save is
   * what establishes the record the documents attach to. A failed save must never reach the
   * delivery.
   *
   * Shared by the two ways a document step is left — Next'ing off it mid-flow, and submitting
   * from it, which is what it became for the advisor once the generation step folded away.
   * One implementation so the ordering rule cannot drift between them.
   *
   * `false` means the caller stays exactly where it is; the failure has already been surfaced,
   * and the advisor's files are still listed so pressing the same button again retries.
   */
  const persistAndDeliver = async (
    step: WorkflowStep,
    outbound?: string,
  ): Promise<'ok' | 'saveFailed' | 'deliveryFailed'> => {
    // Whether a delivery actually follows this save. Gated on `onSave` and `applicationId` for
    // the same reason the uploader itself is: preview never uploaded anything, so it has nothing
    // to deliver.
    const willDeliver = step.type === 'AdditionalDocuments' && !!onSave && !!applicationId;

    // THE PRESS INSTANT, READ EXACTLY ONCE — and only when there is a second call to agree with.
    //
    // It goes out on the save below and again on the deliver call beneath it, and those two
    // compose the SAME §14.1 names — the save as a promise in `bmoData.supportingDocs[].fileName`,
    // the deliver as the name the worker actually SFTPs under. BMO matches deliveries purely by
    // name, so the two have to agree character for character; reading the clock twice, once per
    // request, is what used to leave the save promising `…_NEW_3_…0931` and FileX receiving
    // `…_NEW_3_…0932` with nothing erroring on either side.
    //
    // Minted here rather than server-side because nothing on the server spans the two requests:
    // the ledger row that would anchor them is written BY the deliver call, after the save has
    // already named everything.
    //
    // Left undefined on a save with no delivery behind it. Such a save has nothing to stay in
    // step with, and sending a fresh instant would re-promise a not-yet-delivered upload under a
    // new name on every page turn; the server's creation-instant fallback is steady instead.
    const deliveryTimestamp = willDeliver ? new Date().toISOString() : undefined;

    // The milestone belongs to the step being LEFT, and it goes out in the same save as the
    // values — then it is only committed locally once BMO has accepted it, so a failed save
    // leaves the advisor exactly where they were on both axes.
    if (!(await persistSnapshot(outbound, deliveryTimestamp))) return 'saveFailed';
    if (!willDeliver) return 'ok';
    setSaving(true);
    try {
      // The descriptions are read NOW, not at upload — this call is where §14.1's DOCNAME is
      // composed, so whatever the advisor settled on while they were on the step is what BMO
      // files each document under.
      const deliverables = collectDeliverableFiles(uploadedFiles);
      const outcome = await deliverAndConfirm({
        applicationId,
        templateId: template.id,
        // The same pairing the uploader is handed: the base form owns the keys, the branch target
        // owns the entries. This call is where each entry's LABEL becomes §14.1's DOCNAME and its
        // `isRequired` becomes MANDATORY|OPTIONAL — resolved against the base form, a branched
        // application's named documents silently fell through to the bucket defaults.
        subsequentTemplateId: formBranch?.targetTemplateId ?? null,
        files: deliverables,
        creationTimestamp,
        // The same value the save above just carried — see where it is minted.
        deliveryTimestamp,
      });

      // Close every delivered row's description box. The name has been composed and sent, so an
      // editable box from here would let the row describe one document while BMO holds it under
      // another — the exact drift the editable window was safe from only while nothing had been
      // minted. A Back into the step now shows what BMO was actually given.
      if (outcome.ok && deliverables.length > 0) {
        const delivered = new Set(keysOf(deliverables));
        setUploadedFiles((prev) => ({
          entries: Object.fromEntries(
            Object.entries(prev.entries).map(([entryId, files]) => [
              entryId,
              files.map((f) =>
                f.s3Key && delivered.has(f.s3Key) ? { ...f, deliveryState: 'delivered' } : f,
              ),
            ]),
          ),
          bucket: prev.bucket.map((f) =>
            f.s3Key && delivered.has(f.s3Key) ? { ...f, deliveryState: 'delivered' } : f,
          ),
        }));
      }
      // Reported rather than surfaced here. The ORDERING stays in one place — that is what this
      // helper exists to protect — but the two callers no longer agree on what a failed delivery
      // LOOKS like: Next'ing off a document step mid-flow is a retryable step transition and
      // keeps its toast, while the same failure inside the submit chain is a failed submit and
      // owes the advisor the GAMX support block (`SubmitErrorModal`).
      return outcome.ok ? 'ok' : 'deliveryFailed';
    } finally {
      setSaving(false);
    }
  };

  /**
   * Where Next goes. Ordinarily the next applicable step. After an EDIT from Application Review
   * (INFLOWS-443) it is Review again — "in sequence": every step in between that was already done
   * when EDIT was pressed and is still complete is passed over (and keeps its tick); the first one
   * that the edit left incomplete, or that the advisor has never seen, is where it stops instead.
   */
  const afterEditTarget = (): { target: WorkflowStep; passed: string[] } => {
    const ret = reviewReturnRef.current;
    const plain = { target: nextStep!, passed: [] as string[] };
    if (!ret) return plain;
    const from = allSteps.findIndex((s) => s.id === activeStep.id);
    const to = allSteps.findIndex((s) => s.id === ret.reviewId);
    if (from < 0 || to <= from) return plain;
    const passed: string[] = [];
    for (const s of allSteps.slice(from + 1, to)) {
      if (!isVisible(s)) continue;
      const done =
        ret.walked.has(s.id) &&
        (PASSIVE_STEP_TYPES.has(s.type) ||
          isStepComplete(
            s,
            values,
            uniquenessOffenders,
            uploadedFiles,
            user,
            allSections,
            audience,
            emailLookup,
          ));
      if (!done) return { target: s, passed };
      passed.push(s.id);
    }
    return { target: allSteps[to]!, passed };
  };

  const advanceToNext = () => {
    if (!nextStep || advancing) return;
    if (blockingField) {
      revealBlockingAnswer(blockingField);
      return;
    }
    if (fieldIssues.length > 0) {
      revealMissing();
      return;
    }
    setAdvancing(true);
    void (async () => {
      // The spinner is retired by the step swap (see the layout effect on `activeStepId`), so it
      // only has to be cleared here on the paths where the swap never happens — every abort
      // below, plus anything that throws its way out.
      let advanced = false;
      try {
        // Hand the browser a frame before starting the work, so the spinner is on screen for the
        // part that blocks it. Preview is the case that needs this: with no `onSave` the awaits
        // below all resolve in microtasks, and the incoming step's render would land in the same
        // task as the click — a busy state that never paints is not a busy state.
        await nextPaint();
        // Save, and — leaving a document step — deliver its uploads and wait for BMO to confirm
        // them. The advance blocks on both: an advisor must not be able to reach submit having
        // left documents undelivered, and either failure leaves them exactly where they were, so
        // pressing Next again retries. See `persistAndDeliver`.
        const outbound = advanceStatus(bmoStatusRef.current, milestoneForStep(activeStep.type));
        const persisted = await persistAndDeliver(activeStep, outbound);
        if (persisted !== 'ok') {
          // The save leg has already surfaced its own failure; the delivery leg reports rather
          // than surfaces, so that it can differ between here and the submit chain.
          if (persisted === 'deliveryFailed') {
            void message.error(t('formRenderer.additionalDocuments.deliveryFailed'));
          }
          return;
        }

        // Recomputed over the CURRENT ref rather than committing `outbound` wholesale: the save
        // above may have promoted off APPLICATION_INITIATED, and this step's milestone is usually
        // null (Welcome / DynamicForm), so replaying the outbound value would put the application
        // back on the bottom rung it just left. `advanceStatus` is a max, so the higher of the two
        // wins either way and the milestone is never lost.
        commitStatus((prev) => advanceStatus(prev, milestoneForStep(activeStep.type)));
        const { target, passed } = afterEditTarget();
        setAdvancedSteps((s) => {
          if (s.has(activeStep.id) && passed.every((id) => s.has(id))) return s;
          const next = new Set(s);
          next.add(activeStep.id);
          for (const id of passed) next.add(id);
          return next;
        });
        setActiveStepId(target.id);
        advanced = true;
      } finally {
        if (!advanced) setAdvancing(false);
      }
    })();
  };

  /**
   * The advisor's final submit — everything the old two-step ending did, in one press.
   *
   * It used to take two: Next off Document Upload (save + deliver the uploads), then Submit on
   * the generation step (the point-of-no-return call). The generation step is gone for the
   * advisor, so the three calls are daisy-chained here instead, behind the blocking
   * {@link ProcessingModal} — none of them changed, and nothing new was added.
   *
   *   save  →  deliver documents + confirm  →  submit  →  Thank You
   *
   * Each link gates the next, in spec §14's order. An abort anywhere leaves the advisor on this
   * step with their answers and files intact, so pressing Submit again retries from the top —
   * which is safe at every link: the save is idempotency-keyed, delivery of an
   * already-delivered key is a no-op, and `applicationsApi.submit` is explicitly safe to repeat.
   */
  const submitApplication = () => {
    if (!onSubmit || submitting) return;
    // The submit step now has required content of its own — a mandatory document entry left
    // empty, where the generation step had nothing to fill in at all. Same treatment Next gives
    // it: name what is missing rather than disable the button (QA D-36).
    if (fieldIssues.length > 0) {
      revealMissing();
      return;
    }
    // Client-side mirror of the server's backstop gate (PROJECT_PLAN.md: "same gate re-applied
    // at SUBMIT") — checked across the WHOLE template, not just this step, so a file that
    // regressed after its own step passed is caught before spending a round trip. The server
    // re-checks delivery status independently regardless.
    if (hasBlockedAdditionalDocuments(uploadedFiles)) {
      void message.error(t('formRenderer.pdfGeneration.filesNotReady'));
      return;
    }
    // Wakes the session timer's refresh — see the note on `persistSnapshot` for why this call,
    // not `ensureSessionRefreshed`, is the one to fire here: `applicationsApi` awaits the refresh
    // itself before each request leaves, and pinging again would be the same double-grant bug
    // that once logged an advisor out mid-save.
    notifySessionActivity();
    setSubmitting(true);
    void (async () => {
      try {
        // Get the modal on screen before the chain starts. Same reasoning as `advanceToNext`'s:
        // a busy state that never paints is not a busy state.
        await nextPaint();
        // Links 1 and 2, identical to what Next'ing off this step used to do — including the
        // milestone (DOC-UPLOAD-COMPLETE) travelling in the same save as the values it describes.
        const outbound = advanceStatus(bmoStatusRef.current, milestoneForStep(activeStep.type));
        const persisted = await persistAndDeliver(activeStep, outbound);
        if (persisted !== 'ok') {
          // A delivery that never confirmed is a failed SUBMIT, not a failed page transition:
          // the advisor pressed the last button in the flow and no application reached BMO.
          // Nothing they can do on the step will repair an SFTP push, so it takes the support
          // block. A failed SAVE keeps its own toast — the BFF rejected a payload, which is
          // frequently something the advisor can still act on, and persistSnapshot has already
          // said what it was.
          if (persisted === 'deliveryFailed') setSubmitErrorOpen(true);
          return;
        }
        // Recomputed over the CURRENT ref rather than replaying `outbound`, for the reason spelled
        // out in `advanceToNext`: the save may have promoted off the bottom rung meanwhile.
        commitStatus((prev) => advanceStatus(prev, milestoneForStep(activeStep.type)));
        // Link 3 — the point of no return. Once this resolves BMO has been told the application
        // is Submitted, and the package is either delivered or being redelivered.
        const result = await onSubmit(
          buildApplicationSnapshot(payloadTemplate ?? template, values, pdfLanguage),
          currentESignSubmission(),
          // Keys alone: the submit route identifies the advisor's already-delivered additional
          // files by key, and their §14.1 names were settled by the deliver call above.
          keysOf(collectDeliverableFiles(uploadedFiles)),
          // What the delivered PDFs are filled from, resolved by the same funnel every
          // preview and download uses — so the package cannot differ from what the advisor
          // just previewed. Against the COMPOSED `template`, not `payloadTemplate`: the
          // snapshot persists only the selected form's answers, but a branched
          // application's documents still map the retained prefix's, exactly as the preview
          // passes `template` while requesting against `pdfTemplateId`.
          pdfFillValues(template, values, audience),
        );
        // `advanceStatus` is a max and refuses to move a locked status, which is what stops an
        // already-submitted application from being submitted again if this fires a second time
        // some other way.
        commitStatus((prev) => advanceStatus(prev, BMO_STATUS.submitted));
        void message[result.deliveryStatus === 'delivered' ? 'success' : 'info'](
          result.deliveryStatus === 'delivered'
            ? t('formRenderer.pdfGeneration.submitSuccess')
            : t('formRenderer.pdfGeneration.submitPending'),
        );
        // Land on the terminal confirmation, on exactly the same condition the toast fires: the
        // submit resolved. A `pending` delivery still counts — the application IS submitted, only
        // its package delivery is still finalizing, which is what the pending copy says.
        goToThankYou();
      } catch (err) {
        // Session expiry runs its own warning-modal / redirect flow (ADR-0022); a "submit failed"
        // toast over it would tell the advisor the wrong thing.
        if (err instanceof ApplicationSessionExpiredError) return;
        // Everything else splits two ways — see `classifySubmitFailure` for where the line is and
        // why an UNRECOGNISED failure lands on the support block rather than the toast.
        const surface = classifySubmitFailure(err);
        if (surface.kind === 'advisorActionable') {
          void message.error(t(surface.messageKey));
          return;
        }
        // `sessionFault` gets its own copy rather than the support block: BMO refusing the
        // advisor's ISAM token is not something GAMX support can help with, and it is the same
        // set of codes the BFF excludes from raising an incident. Not silent, though — unlike a
        // BFF session expiry there is no redirect running behind this, so silence would leave
        // the advisor with a dismissed spinner and no explanation.
        setSubmitErrorVariant(surface.kind === 'sessionFault' ? 'session' : 'technical');
        setSubmitErrorOpen(true);
      } finally {
        setSubmitting(false);
      }
    })();
  };
  // Rail step list — every step this runtime will actually walk, minus Thank You outside
  // preview.
  //
  // Hidden means ABSENT, for every audience alike. The history, because this has now been
  // flipped twice and the next reader deserves the whole trail:
  //
  //   2026-07-15 (user)  : preview drops hidden steps, so the previewer sees exactly an
  //                        applicant's flow.
  //   2026-08-05 (Ugur)  : reversed — a step that does not apply is SHOWN but disabled, so the
  //                        reader can see what their answers switched off.
  //   2026-10-09 (user)  : reversed again, and this time platform-wide rather than
  //                        preview-only. A step the flow will SKIP must not be listed at all,
  //                        in originator preview, approver inline preview, the advisor runtime
  //                        and the unauthenticated customer runtime. A greyed row that can
  //                        never be opened is noise in a left nav whose one job is "here is
  //                        what you have to fill in"; worse, on a long workflow it inflates
  //                        the apparent length of an application that is actually short.
  //
  // There is only ONE mount-site consequence to check, and it is the same line in all four
  // hosts: they each pass `mode` and nothing else that bears on this, so filtering here
  // covers all of them at once — `audience` and the condition walk are already composed
  // together by `isVisible` (via `stepVisibility`), which is also what drives Next/Back and
  // the active-step fallback. So the rail and the navigation chain now list exactly the same
  // steps by construction, which is the property that was missing before.
  //
  // Welcome is listed too, under its own title ("Introduction" on RDSP): BMO's stories name it
  // as the first left-nav entry (RDSP 139, comment 18/Jun). The rail is hidden while it is the
  // active step (a full-bleed intro card with its own CANCEL / CONTINUE), so its row is a way
  // BACK to the language choice; CONTINUE returns to the flow, and the steps already passed
  // stay reachable from the rail.
  //
  // Thank You is dropped too (BMO, 2026-09-29): it is where the flow ENDS, not a step the
  // advisor works through, and the stories' left nav stops at Document Upload. Kept in PREVIEW,
  // where the rail is the originator's only way to see that page.
  //
  // Plain filter, not a useMemo — this sits after the `!activeStep` early
  // return above, so a hook here would break hook order.
  const railSteps = allSteps.filter(
    (s) => (s.type !== 'ThankYou' || mode === 'preview') && isVisible(s),
  );
  // Tooltip priority: step-level blocker (e.g. email verify) > dead-end
  // answer > unfilled-required > no-target > none. Step-level takes the top
  // slot because the reason is authored copy tailored to that step's
  // failure. Show up to 3 specific field labels for "unfilled"; collapse to
  // a generic message when there are more so the tooltip stays readable.
  // Nothing while the button is merely busy: a save in flight is not a reason the advisor has to
  // fix, and the fall-through below would otherwise explain it as "no next step".
  let nextTooltip = '';
  if (nextDisabled && !nextBusy) {
    if (stepBlocker) {
      nextTooltip = stepBlocker;
    } else if (fieldIssues.length > 0) {
      nextTooltip = nextBlockedMessage(fieldIssues, t);
    } else {
      nextTooltip = t('formRenderer.nextBlockedNoTarget');
    }
  }

  return (
    // Single white surface wrapping both rail + form, with a grey vertical
    // divider between them — matches the BMO design reference where the step
    // rail and main form read as a unified card, not two floating panels.
    // `flex: 1` + `minHeight` makes the card stretch to fill the available
    // content area so the rail extends down to the footer instead of
    // floating as a short panel on tall viewports.
    // `.form-shell` is a CSS container (`container-type: inline-size`); all the
    // responsive reflow — rail → floating overlay, nav buttons stacking, main
    // padding — lives in `@container` rules in theme/global.css so it fires off
    // THIS element's own width wherever the shell is mounted (device-preview
    // frame or a genuinely narrow viewport), with no JS measurement. The only
    // JS state left is `railOpen`, surfaced as the `--rail-open` modifier.
    <div className={`form-shell${railOpen ? ' form-shell--rail-open' : ''}`}>
      {/* Rail (+ its mobile toggle/scrim) is hidden entirely on the two steps that
          book-end the flow:

            • Welcome — no notion of "other steps" to navigate to yet.
            • ThankYou (2026-11-02, user) — the flow is OVER. In the advisor runtime the
              rail was already dead by this point (`railLocked` disables every row once
              the active step is ThankYou), so a column of unclickable grey titles next
              to "your package has been submitted" was pure noise. Hidden in PREVIEW too,
              and deliberately: preview exists to show the originator what the advisor
              sees, and an originator-only rail here would show them a page that does not
              exist. The cost is that reaching Thank You in preview is one-way — the
              preview host's own Back/close is the way out (PreviewPage's header button,
              the approver modal's close), the same exit the originator already uses.

          ThankYou stays IN `railSteps` (above): that row is how preview gets here at
          all, and it is only ever rendered from an earlier step. */}
      {activeStep.type !== 'Welcome' && activeStep.type !== 'ThankYou' && (
        <>
          {/* The narrow-shell DOCK, carrying the toggle. A zero-size `position: sticky`
              flex item: it reserves no layout space (so the button still FLOATS over the
              form rather than claiming a column), but it rides the page scroll and pins
              at the top of the viewport the moment the card's own top goes by.

              `display: contents` on a wide shell is what lets one tree serve both
              layouts: the box vanishes and the rail goes back to being an in-flow 280px
              column of `.form-shell`, exactly as if the wrappers weren't there.

              A true viewport-wide drawer (`position: fixed`) is not available and would
              not help: `.form-shell` carries `container-type: inline-size`, which implies
              `contain: layout`, which makes the shell the containing block for
              fixed-position descendants — a fixed rail resolves against the card anyway.
              Escaping that means portalling the rail out of the shell, which costs it the
              container query that decides it should be a drawer in the first place.

              Keeping all of this CSS-only — no scroll listener, no measurement — is why
              `.form-shell` uses `overflow: clip` rather than `hidden`; see
              theme/global.css. */}
          <div className="form-shell__rail-dock">
            {/* Floating rail toggle — hidden by CSS until the shell is narrow, then
                pinned top-left to open the rail overlay. */}
            <Tooltip title={t('formRenderer.showSteps')}>
              <Button
                className="form-shell__rail-toggle"
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setRailOpen((open) => !open)}
                aria-label={t('formRenderer.showSteps')}
              />
            </Tooltip>
          </div>
          {/* The drawer's TRACK — a sibling of the dock, not a child of it, and that is
              load-bearing: it spans the CARD (`top: 0; bottom: 0`) in zero width, which
              is the bound the drawer inside it sticks within. A zero-HEIGHT dock cannot
              express that bound, which is how the panel came to be sized against the
              viewport and clipped by the card: on a short card the bottom of the step
              list was laid out, painted nowhere, and unselectable, with no overflow for
              `overflow-y` to surface. See `.form-shell__rail-track` in global.css.

              The toggle stays on the dock so its own pinning is untouched — it keeps
              riding to the very last pixel of the card, where a panel-height track would
              have started lifting it a screenful early. */}
          <div className="form-shell__rail-track">
            {/* One rail: an in-flow column when wide, a sticky slide-in overlay when
                narrow — the switch is entirely CSS. */}
            <div className="form-shell__rail">
              {/* Dismiss, sitting in the drawer's own top strip — the band of white the
                  rail already reserves so its first step clears the floating toggle.
                  Hidden by CSS on a wide shell, where there is no drawer to close and
                  the rail is simply a column of the card.

                  A third way out alongside the toggle and the scrim, and the only one
                  that reads as part of the panel: the toggle is chrome BEHIND the
                  overlay and the scrim is an invisible pointer target, so neither
                  announces itself as "this closes". Rendered unconditionally and shown
                  by container query, like every other piece of this shell's reflow —
                  no viewport read. */}
              <Tooltip title={t('formRenderer.hideSteps')}>
                <Button
                  className="form-shell__rail-close"
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={() => setRailOpen(false)}
                  aria-label={t('formRenderer.hideSteps')}
                />
              </Tooltip>
              <StepRail
                steps={railSteps}
                lockedStepIds={railLocked}
                activeStepId={activeStep.id}
                language={language}
                stepCompletion={stepCompletion}
                // 2026-08-05: the rail is clickable in BOTH modes now. It used to be
                // `mode === 'preview'`, so in the advisor runtime no entry could be
                // clicked at all. Per Ugur: steps are clickable by default. A step whose
                // conditions are not met no longer reaches the rail at all (see
                // `railSteps`), so this can only ever land on an applicable step; the one
                // remaining per-row refusal is `lockedStepIds` (not reached yet).
                onSelect={(id) => {
                  setActiveStepId(id);
                  // Harmless when wide (rail is static, overlay transform unused).
                  setRailOpen(false);
                }}
                clickable
              />
            </div>
          </div>
          {/* Scrim — hidden by CSS unless narrow AND open; click to close. Its only
              job is "click outside to dismiss", which is a pointer affordance with
              no keyboard equivalent to duplicate: a keyboard user never reaches the
              overlay state's trap because the rail toggle itself is a real button
              and Escape closes it (below). Marked aria-hidden so it isn't announced
              as a stray interactive element.

              Stays a child of the SHELL rather than joining the dock: its job is to
              cover the form, and the form is the full height of the card. */}
          <div aria-hidden className="form-shell__rail-scrim" onClick={() => setRailOpen(false)} />
        </>
      )}
      <main ref={mainRef} className="form-shell__main">
        {/* The drawer's stand-in on a narrow shell: "Step 4 of 12 - Document Upload" plus
            a segment per step. Hidden by CSS above 900px, where the rail is an in-flow
            column already saying the same thing — see StepProgress for the whole of that
            argument, and why it is deliberately not a second navigator.

            Fed `railSteps`, so it counts exactly what the drawer lists. Skipped on Welcome
            and ThankYou for the same reason the rail is: neither is part of the numbered
            walk, and with the drawer gone this would be a counter standing in for nothing.

            ThankYou needs the explicit check even though it self-suppresses when absent
            from `railSteps` (an unlisted active step renders null): in PREVIEW the row IS
            listed, so without this the confirmation kept a "Step 5 of 5 - Thank You" bar
            above it and a toggle that opens nothing. */}
        {activeStep.type !== 'Welcome' && activeStep.type !== 'ThankYou' && (
          <StepProgress steps={railSteps} activeStepId={activeStep.id} language={language} />
        )}
        {/* Locked applications still render in full — the advisor can read every answer and walk
            the rail — they just can't change anything. The banner says why, because an inert
            form with no Save button and no explanation reads as a broken page.

            ThankYou is the one step that needs no such explanation, so it is excluded (QA,
            2026-09-15). Nothing there looks broken: the card right underneath already says the
            package has been completed and submitted, and there are no inputs to wonder about.
            Stacking "this can no longer be edited" on top of "thank you, it's submitted" just
            reads as a warning about a step that went right. */}
        {locked && activeStep.type !== 'ThankYou' && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('formRenderer.locked.title')}
            description={t(`formRenderer.locked.${lockedReasonKey(status)}`, { status })}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <ShowErrorsContext.Provider value={showErrorsTick}>
            {/* The client's language reaches every field renderer (option language scopes,
                QA D-30) without threading it through each step's prop bag. */}
            <ClientLanguageContext.Provider value={offerLanguages}>
              {/* The workflow's date format (QA D-01) — every Date control and the review
                  recap display and accept dates in it; storage stays ISO. */}
              <DateFormatContext.Provider value={resolveDateFormat(template.dateFormat)}>
                {/* ZeroBounce on blur, for every email input below. Null when the runtime may
                    not verify (the public customer), which is the whole switch — no leaf
                    needs to know whose runtime it is. */}
                <EmailVerifyContext.Provider value={emailVerify}>
                  <RestartFromBlockContext.Provider value={restartFromBlock}>
                    <StepContent
                      // Re-mount on step switch so internal field components (date
                      // pickers, modals, upload trackers) cleanly reset their local
                      // UI state across step navigation.
                      key={activeStep.id}
                      template={template}
                      step={activeStep}
                      values={values}
                      onSetStepBlocker={setStepBlocker}
                      generatePdf={generatePdf}
                      pdfTemplateId={pdfTemplateId}
                      addressAutocomplete={addressAutocomplete}
                      audience={audience}
                      onChange={(fieldId, next, opts) => {
                        // Two guards before this counts as an advisor edit, and they catch different
                        // things. `programmatic` is the explicit intent flag, set by writes the SHELL
                        // causes rather than the advisor — those fire precisely when a slot is empty,
                        // so no value comparison could tell them apart from real typing. (No step sets
                        // it today; it was the eSign step's sole-rep-code backfill, removed with the
                        // rep-code column.) The comparison then catches a no-op re-set: a
                        // Select re-picking what it already held would otherwise cost the advisor a rung
                        // for a click that changed nothing.
                        if (!opts?.programmatic && !Object.is(values[fieldId], next))
                          demoteForActiveStep();
                        // Mark the field as manually edited so cross-step prefill
                        // stops overwriting it. Guarded so we only tick state when
                        // we're actually FLIPPING an unedited slot to edited —
                        // otherwise every keystroke would bump the tick.
                        if (!editedFieldsRef.current.has(fieldId)) {
                          editedFieldsRef.current.add(fieldId);
                          setEditedTick((t) => t + 1);
                        }
                        // `resetStepId` (opt-in): this answer OWNS another step, so a real
                        // change hands that step back to the shell — every answer dropped and
                        // every freeze lifted, so the prefill and default passes refill it on
                        // the next render as if it had never been opened. Done outside
                        // `setValues` because the freezes live in refs, and before it so the
                        // prefill effect observes the unfrozen step and the wiped values
                        // together. Guarded on an actual change: re-picking the same option
                        // must not throw away the step underneath it.
                        const resetStep = resetStepFor(allSteps, fieldId);
                        if (resetStep && !Object.is(values[fieldId], next)) {
                          shownStepsRef.current.delete(resetStep.id);
                          for (const key of stepAnswerKeys(resetStep, values)) {
                            editedFieldsRef.current.delete(key);
                            prefillCopiesRef.current.delete(key);
                            delete restoredValuesRef.current[key];
                          }
                        }
                        // `clearHiddenAnswers` (opt-in): answers this one now hides are emptied.
                        //
                        // An inline reveal this answer just CLOSED is deliberately NOT emptied
                        // (BMO #304/#305): re-ticking the option brings back what was entered.
                        // Closing a reveal used to wipe it unconditionally, which no setting
                        // could switch off — a `revealFieldId` / `revealSectionId` target carries
                        // no `conditions` of its own, so `clearHiddenAnswers` was structurally
                        // unable to reach it in either position. Retaining is safe because
                        // nothing downstream can read a closed reveal: `hiddenBorrowedFieldIds`
                        // prunes it from required-completeness, the Application Review, the
                        // snapshot and the PDFs. See `hiddenReveals.ts`.
                        //
                        // The `resetStepId` wipe runs last: it is a superset of the clear above
                        // for the step it owns, and the clear is still needed for everything
                        // this answer hides ELSEWHERE.
                        setValues((prev) => {
                          const settled = clearAnswersHiddenBy(allSteps, fieldId, {
                            ...prev,
                            [fieldId]: next,
                          });
                          if (!resetStep || Object.is(values[fieldId], next)) return settled;
                          return clearStepAnswers(resetStep, settled);
                        });
                      }}
                      onJumpToStep={(id) => {
                        if (activeStep.type === 'ApplicationReview')
                          reviewReturnRef.current = {
                            reviewId: activeStep.id,
                            walked: new Set(advancedSteps),
                          };
                        setActiveStepId(id);
                      }}
                      language={language}
                      mode={rendererMode}
                      // Not folded into `locked` (:475): that also hides the whole nav row and
                      // paints the locked banner, which would strand the advisor on a step they
                      // are meant to be able to walk straight past.
                      readOnly={locked || (stepDisabled.get(activeStep.id) ?? false)}
                      pdfCache={pdfCacheRef.current}
                      findUniquenessConflictMessage={findUniquenessConflictMessage}
                      mutateValues={(updater) => {
                        // Only wired to a repeatable section's Add / Remove buttons, which are always a
                        // deliberate structural change — no equality check to make, and deliberately no
                        // comparison INSIDE the updater: calling a setter from within another setter's
                        // updater is double-invoked under StrictMode.
                        demoteForActiveStep();
                        setValues((prev) => updater(prev));
                      }}
                      uploadedFiles={uploadedFiles}
                      // Wraps the setter for the STEP only. `useAdditionalDocumentScanStatus` above is
                      // handed the raw `setUploadedFiles` on purpose: it patches malware-scan verdicts on
                      // a background poll that keeps running across step navigation, and routing that
                      // through here would demote the application — and re-lock the advisor's rail —
                      // every few seconds while a scan resolves.
                      onUploadedFilesChange={(updater) => {
                        demoteForActiveStep();
                        setUploadedFiles(updater);
                      }}
                      additionalDocumentUploader={additionalDocumentUploader}
                      pdfLanguage={pdfLanguage}
                      onPdfLanguageChange={(next) => {
                        // The Welcome step's language radio rewrites every label in the snapshot, so it is
                        // an edit. Guarded because a radio can re-emit its current value.
                        if (next !== pdfLanguage) demoteForActiveStep();
                        setPdfLanguage(next);
                      }}
                      onContinue={advanceToNext}
                      onCancelExit={onWelcomeCancel ?? onSaveAndExit}
                      onDashboardExit={onSaveAndExit}
                    />
                  </RestartFromBlockContext.Provider>
                </EmailVerifyContext.Provider>
              </DateFormatContext.Provider>
            </ClientLanguageContext.Provider>
          </ShowErrorsContext.Provider>
          {/* TODO(BMO-PRELAUNCH-DIAGNOSTICS): remove with the probe endpoint before go-live —
              deleting this block and the import is the whole removal on this side.
              Dev-only, and on the SUBMIT step deliberately: by then the roster has resolved and
              the documents are listed, so the composed eSignInfo can be checked against what the
              advisor is about to submit. Keyed off `submitStep` rather than the PdfGeneration
              type so it follows the action when the generation step is folded away (advisor) and
              stays put where it is not (preview). Rendered in both modes — an originator
              or approver hits it from preview, where the probe writes nothing at all.
              Sits outside StepContent so it doesn't need any of that component's prop bag. */}
          {/* `mode !== 'public'` is belt-and-braces on top of the runtime-config gate:
              the probe composes the signatory package (names, emails, phones) and posts
              it to a diagnostics route, neither of which belongs anywhere near an
              anonymous customer even in a non-prod environment. */}
          {activeStep.id === submitStep?.id && mode !== 'public' && isESignInfoProbeEnabled() && (
            <ESignInfoProbe
              // The BASE form, exactly as the save sends it: `template` is the composed one on a
              // branched form, and `composeBranchedTemplate` pins its `id` to the base form's.
              templateId={template.id}
              // ...and the branch target alongside it, so the server composes the block against
              // the form the advisor actually filled. Read from `formBranch` for the same reason
              // `pdfTemplateId` is — it is the only thing here that names the target.
              subsequentTemplateId={formBranch?.targetTemplateId ?? null}
              // The PDF language, not the advisor's UI locale: it decides which language's
              // placements survive and what locale each signer record carries.
              language={pdfLanguage}
              applicationId={applicationId}
              creationTimestamp={creationTimestamp}
              // Gates eSignInfo server-side, so the probe has to send the same rung the next save
              // would. The render mirror rather than the ref: this is a render, and the two only
              // differ mid-flight.
              bmoStatus={status}
              buildSubmission={currentESignSubmission}
            />
          )}
        </div>
        {/* Nav row — Back on the left, Save and exit + Next on the right.
            Both Save and exit and Next push the full application snapshot to BMO
            (ADR-0010) and only then navigate. Disabled states at flow edges, plus
            while a save is in flight. When the shell is collapsed (mobile-width
            preview, or a phone) the row becomes a full-width STACK in priority
            order — the primary action on top, then Save and exit, then Back.
            That reversal is done in CSS from this DOM, so there is one tree for
            both layouts; see `.form-shell__nav` in global.css, which owns the
            arrangement outright (an inline `align-items` here would beat the
            container query that has to stack it).
            Hidden entirely on the Welcome step — its card owns its own
            CANCEL / CONTINUE actions.
            Hidden on a locked application too: there is nothing left to save and nothing to
            advance to, and Back would be the only survivor — redundant, since a locked
            application unlocks the whole rail (see `railLocked`) and every step is one click
            away there. */}
        {/* ThankYou is terminal — its own two CTAs leave the form, and every button here
            would be wrong on it: Back walks out of a submitted application, Save and exit
            re-saves what BMO already owns, and there is nothing to advance to.
            In the ADVISOR path `locked` alone would cover this, because a successful submit
            sets SUBMITTED and that locks the shell. The explicit check is what covers
            PREVIEW, where nothing is locked and an originator can click straight to this
            step in the rail. */}
        {activeStep.type !== 'Welcome' && activeStep.type !== 'ThankYou' && !locked && (
          <div className="form-shell__nav">
            <Button
              icon={<ArrowLeftOutlined />}
              disabled={!prevStep}
              onClick={() => prevStep && setActiveStepId(prevStep.id)}
            >
              {t('formRenderer.back')}
            </Button>
            <div className="form-shell__nav-actions">
              {/* Absence is the gate, the same idiom as onSave and onSubmit. A public
                  customer has nothing to save and nowhere to exit to, so the button must
                  not exist rather than sit there disabled. Behaviour-preserving for the
                  other two audiences: advisor and preview both pass onSaveAndExit. */}
              {onSaveAndExit && (
                <Button
                  icon={<SaveOutlined />}
                  loading={saving}
                  disabled={saving}
                  onClick={() => {
                    // Exit only once BMO holds the snapshot — leaving on a failed save would
                    // drop the advisor back on the dashboard with their work gone.
                    void (async () => {
                      if (!(await persistSnapshot())) return;
                      void message.success(t('formRenderer.draftSaved'));
                      onSaveAndExit?.();
                    })();
                  }}
                >
                  {t('formRenderer.saveAndExit')}
                </Button>
              )}
              {activeStep.id === submitStep?.id ? (
                onSubmit ? (
                  // The real final submit (advisor runtime only — see onSubmit's doc comment).
                  // One press runs the whole ending: save → deliver documents → submit → Thank
                  // You. See `submitApplication`, which owns the chain and the gates.
                  //
                  // `stepBlocker` is honoured here as well as on Next: a file still scanning is
                  // the submit step's own reason to wait, and the tooltip beside it says so.
                  <Tooltip title={stepBlocker ?? ''}>
                    <span style={{ display: 'inline-block' }}>
                      <Button
                        type="primary"
                        icon={<CheckCircleFilled />}
                        loading={submitting}
                        disabled={submitting || saving || stepBlocker !== null}
                        onClick={submitApplication}
                      >
                        {submitting
                          ? t('formRenderer.pdfGeneration.submitting')
                          : t('formRenderer.pdfGeneration.submit')}
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  // Preview / originator / approver (no onSubmit — see its doc comment): keep the
                  // bulk-download stand-in so reviewers can still grab every generated PDF at once
                  // to sanity-check a workflow's configuration. Runs the same fetch → pdf-lib fill →
                  // download pipeline as the per-row buttons, once per PDF this application's
                  // answers generate (`applicablePdfDocs` — the same list the PDF step shows). Missing
                  // files toast the stub message; any fetch/fill error surfaces via the generic
                  // preview-failed copy.
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={() => {
                      void (async () => {
                        // Client's chosen PDF language (from the Welcome step
                        // radio, if authored — otherwise the mount-time
                        // initial) drives the S3 template pick, not the
                        // advisor's UI language.
                        const docs = applicablePdfDocs(template, pdfLanguage, values);
                        const withFiles = docs.filter((d) => d.previewKey);
                        // Flatten uploaded documents so the zip bundle
                        // contains BOTH the pdf-lib-filled templates AND
                        // every file the advisor picked on any
                        // AdditionalDocuments step earlier in the flow.
                        // Each named entry contributes its filenameTemplate
                        // so a mandatory Transfer form / Void cheque gets
                        // renamed inside the zip; optional-bucket files
                        // keep their original names.
                        const uploaded = flattenUploadedFiles(
                          uploadedFiles,
                          collectEntryMeta(template),
                          values,
                          template,
                        );
                        if (withFiles.length === 0 && uploaded.length === 0) {
                          void message.info(t('formRenderer.pdfGeneration.stubDownloadAll'));
                          return;
                        }
                        try {
                          // Everything goes into a single zip so the advisor
                          // gets one file, not N sequential downloads that
                          // browsers rate-limit and misorder. Predictable
                          // shape for the caller: Download all → one zip,
                          // no matter the count. Per-row Download button on
                          // PdfGenerationStep still delivers the templated
                          // single file directly when the advisor only
                          // wants one.
                          const zip = new JSZip();
                          // De-dupe collisions on filename (two mandatory
                          // rows named "form.pdf" or two bucket uploads
                          // sharing a name) — append " (2)" / " (3)" etc.
                          // before the extension.
                          const usedNames = new Set<string>();
                          const uniqueName = (raw: string): string => {
                            if (!usedNames.has(raw)) {
                              usedNames.add(raw);
                              return raw;
                            }
                            const dot = raw.lastIndexOf('.');
                            const stem = dot >= 0 ? raw.slice(0, dot) : raw;
                            const ext = dot >= 0 ? raw.slice(dot) : '';
                            let i = 2;
                            while (usedNames.has(`${stem} (${i})${ext}`)) i += 1;
                            const candidate = `${stem} (${i})${ext}`;
                            usedNames.add(candidate);
                            return candidate;
                          };
                          for (const d of withFiles) {
                            // Reuse the PDF-lib cache when possible — the
                            // PdfGeneration step normally pre-generates by
                            // the time the user hits Download all. Cache
                            // miss or stale falls through to a fresh fill;
                            // the result is written back so the per-row
                            // buttons see it too.
                            const existing = pdfCacheRef.current.get(d.entryId);
                            let blobUrl: string;
                            if (
                              existing &&
                              existing.values === values &&
                              existing.language === pdfLanguage
                            ) {
                              blobUrl = existing.url;
                            } else {
                              blobUrl = await buildFilledPdfBlobUrl(
                                template,
                                d.previewKey!,
                                values,
                                d.entryId,
                                d.previewLanguage,
                                generatePdf,
                                audience,
                                pdfTemplateId,
                              );
                              if (existing) URL.revokeObjectURL(existing.url);
                              pdfCacheRef.current.set(d.entryId, {
                                values,
                                language: pdfLanguage,
                                url: blobUrl,
                              });
                            }
                            const bytes = await (await fetch(blobUrl)).arrayBuffer();
                            zip.file(uniqueName(d.fileName), bytes);
                          }
                          for (const u of uploaded) {
                            zip.file(uniqueName(u.fileName), u.file);
                          }
                          const zipBlob = await zip.generateAsync({
                            type: 'blob',
                            compression: 'DEFLATE',
                            compressionOptions: { level: 6 },
                          });
                          const zipUrl = URL.createObjectURL(zipBlob);
                          // The workflow's package name template when authored
                          // (`packageZipName`). Otherwise the zip follows the same
                          // shape as the individual PDFs — the first PDF entry's
                          // rendered stem + `.zip`, e.g. `RDSP1_Smith_07072026.zip`
                          // — falling back to the first uploaded file's stem OR the
                          // appName when no PDFs are present.
                          const firstNamed =
                            withFiles[0]?.fileName ??
                            uploaded[0]?.fileName ??
                            `${template.appName || 'workflow'}-documents.pdf`;
                          const stem = firstNamed.replace(/\.[^.]+$/, '');
                          const zipName = packageZipName(template, values, stem);
                          triggerBlobDownload(zipUrl, zipName);
                          // Give the browser time to hand the download to
                          // the OS before we revoke.
                          setTimeout(() => URL.revokeObjectURL(zipUrl), 5_000);
                          // For a public customer this zip IS the end of the flow —
                          // there is no Submit, nothing was sent anywhere, and the
                          // files in their downloads folder are the whole deliverable.
                          // So finish on the confirmation card that tells them to take
                          // the printed forms to a branch.
                          //
                          // Public only: an originator sanity-checking a workflow's PDFs
                          // is mid-review and must not be teleported to a thank-you
                          // card. Inside the try and after the download, so a failed
                          // zip leaves them on this step to retry.
                          if (mode === 'public') goToThankYou();
                        } catch {
                          void message.error(t('formRenderer.pdfGeneration.previewFailed'));
                        }
                      })();
                    }}
                  >
                    {/* The customer needs to know this button also ENDS the session,
                        since it is the last thing they do and nothing is saved. */}
                    {t(
                      mode === 'public'
                        ? 'formRenderer.pdfGeneration.downloadAllAndFinish'
                        : 'formRenderer.pdfGeneration.downloadAll',
                    )}
                  </Button>
                )
              ) : (
                <Tooltip title={nextDisabled ? nextTooltip : ''}>
                  {/* AntD disables tooltip on a disabled button by default —
                    wrap in a span so the tooltip still fires on hover over
                    the disabled state. */}
                  <span style={{ display: 'inline-block' }}>
                    <Button
                      type="primary"
                      icon={<ArrowRightOutlined />}
                      // Swaps the arrow for a spinner while the advance is in flight — the save
                      // in advisor mode, the incoming step's render in preview. Both read as a
                      // dead click without it.
                      loading={advancing}
                      disabled={nextDisabled}
                      onClick={advanceToNext}
                      style={nextDisabled ? { pointerEvents: 'none' } : undefined}
                    >
                      {t('formRenderer.next')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        )}
      </main>
      {/* Option redirect — the advisor picked an answer the originator marked as
          belonging on a different form. Rendered at SHELL level, not beside the
          radio: it is a dialog about the whole application, and its outcome
          takes the advisor off this form entirely.

          No "dismissed" bookkeeping: declining CLEARS the answer, which stops
          the rule matching, which unmounts this. Re-picking the same option
          re-triggers it, which is right — the answer still belongs elsewhere,
          and the second ask is the advisor's own doing. Withheld entirely when
          the host gave us nowhere to go (see `onOptionRedirect`). */}
      {activeRedirect && onOptionRedirect && (
        <OptionRedirectModal
          body={redirectMessageFor(activeRedirect.option, language)}
          onGo={() =>
            onOptionRedirect({
              templateId: activeRedirect.templateId,
              url: activeRedirect.url,
            })
          }
          onCancel={() => {
            // Clearing is an advisor edit like any other — it costs a rung on
            // the status ladder, same as typing over the answer would.
            demoteForActiveStep();
            setValues((prev) => ({ ...prev, [activeRedirect.field.id]: null }));
          }}
        />
      )}
      {/* The final submit is a chain of three calls, not one (see `submitApplication`), and the
          advisor must not navigate away or close the tab part-way through it. Shell level, like
          the redirect dialog above: it is about the whole application, not about the step. */}
      {submitting && <ProcessingModal />}
      {/* Its successor on a technical failure: the same press, the same shell level, the other
          outcome. Never both at once — the chain has settled by the time this is raised. */}
      {submitErrorOpen && (
        <SubmitErrorModal variant={submitErrorVariant} onClose={() => setSubmitErrorOpen(false)} />
      )}
    </div>
  );
}

/**
 * The redirect dialog. Two ways out, both terminal for this answer: GO leaves
 * for the destination, CANCEL clears the answer (the caller's handler) so the
 * advisor is back to an unanswered question rather than sitting on one this
 * form cannot carry.
 *
 * Deliberately not dismissible by mask click or Escape — both read as "not
 * now", and there is no "not now" here. The × is likewise absent: with cancel
 * wired to a real state change, a close affordance that did something
 * DIFFERENT from the Cancel button would be a trap. Styled after
 * FieldRenderer's BlockContinueAlertModal, its nearest sibling.
 */
function OptionRedirectModal({
  body,
  onGo,
  onCancel,
}: {
  /** Originator-authored explanation; null falls back to generic copy. */
  body: string | null;
  onGo: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open
      closable={false}
      maskClosable={false}
      keyboard={false}
      onCancel={onCancel}
      width={480}
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <SwapOutlined style={{ color: '#0179C1', fontSize: 20 }} />
          {t('formRenderer.optionRedirectTitle')}
        </span>
      }
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t('formRenderer.optionRedirectCancel')}
        </Button>,
        <Button key="go" type="primary" onClick={onGo}>
          {t('formRenderer.optionRedirectGo')}
        </Button>,
      ]}
    >
      <div style={{ lineHeight: 1.5, color: '#1f2937', paddingTop: 4 }}>
        {body ?? t('formRenderer.optionRedirectBody')}
      </div>
    </Modal>
  );
}

/**
 * Renders the active step's body. Dispatches by `step.type` — DynamicForm
 * routes to the form renderer; AdditionalDocuments to the file-upload UI;
 * every other type renders a placeholder card. Centralizes the dispatch so
 * FormShell stays compact.
 */
/**
 * Dead write handlers for a locked application. Module-level constants rather than inline
 * arrows so their identity is stable — FormRenderer and its field tree memoize on these props,
 * and a fresh closure per render would re-render every field on every keystroke elsewhere.
 *
 * Two of the step branches below withhold their handler entirely (undefined), because those
 * components already read absence as "no write path". These two exist for the branches whose
 * prop is required.
 */
const noopFieldChange = (): void => {};
const noopUploadedFilesChange = (): void => {};

function StepContent({
  template,
  step,
  values,
  onChange,
  onJumpToStep,
  onSetStepBlocker,
  language,
  mode,
  readOnly,
  pdfCache,
  findUniquenessConflictMessage,
  mutateValues,
  uploadedFiles,
  onUploadedFilesChange,
  additionalDocumentUploader,
  pdfLanguage,
  onPdfLanguageChange,
  onContinue,
  onCancelExit,
  onDashboardExit,
  generatePdf,
  addressAutocomplete,
  audience,
  pdfTemplateId,
}: {
  template: WorkflowTemplate;
  step: WorkflowStep;
  values: Record<string, FieldValue>;
  /** `programmatic` marks a write the SHELL caused rather than the advisor — it must not cost
   *  them a rung on the status ladder. See FormShell's handler and OneSpanESignStep. */
  onChange: (fieldId: string, next: FieldValue, opts?: { programmatic?: boolean }) => void;
  /** ApplicationReview row's edit pencil jumps back to the originating step. */
  onJumpToStep: (stepId: string) => void;
  /** Step-level Next blocker. Currently only OneSpanESignStep uses this
   *  (INFLOWS-188 email verification). Pass null when the step is ready to
   *  advance; a non-null string is used as the disabled-button tooltip. */
  onSetStepBlocker: (reason: string | null) => void;
  language: 'en' | 'fr';
  mode: RendererMode;
  /** The application is locked (BMO owns it) or the caller asked for read-only. Each branch
   *  below withholds its own write handlers rather than passing a flag down, so a step that
   *  cannot write has no write path to reach — the same reasoning as
   *  {@link useAdditionalDocumentUpload}'s absence-in-preview. */
  readOnly: boolean;
  /** FormShell-owned generated-PDF cache — passed through so PdfGenerationStep
   *  can seed its ready map on remount without re-requesting generation for
   *  values that haven't changed. Since ADR-0031 that request is a round trip to
   *  the API and a Lambda invocation, so the cache saves rather more than the
   *  local CPU it used to. */
  pdfCache: PdfCache;
  /** Which endpoint fills the documents. Resolved once by FormShell (authenticated by
   *  default, anonymous for the public runtime) and passed straight through, so no step
   *  ever chooses an API client for itself. */
  generatePdf: PdfGenerator;
  /** Which workflow a fill is requested against. Resolved once by FormShell and passed
   *  straight through, same as `generatePdf` — differs from `template.id` only on a
   *  branched form. See `buildFilledPdfBlobUrl`'s `requestTemplateId`. */
  pdfTemplateId: string;
  /** See {@link Props.addressAutocomplete}. */
  addressAutocomplete?: boolean;
  /** Which audience this runtime serves. Needed separately from `mode`, which arrives here
   *  already narrowed by rendererModeFor - public and advisor are indistinguishable by that
   *  point, and the ThankYou copy has to tell them apart. */
  audience: StepAudience;
  findUniquenessConflictMessage?: (
    field: WorkflowField,
    value: FieldValue,
    ownValueKey?: string,
  ) => string | null;
  mutateValues?: (
    updater: (prev: Record<string, FieldValue>) => Record<string, FieldValue>,
  ) => void;
  /** Present only in advisor runtime; absent in preview, which is what keeps preview from
   *  writing to the customer-PII bucket. */
  additionalDocumentUploader?: AdditionalDocumentUploader;
  /** Shared advisor-uploaded document store — see FormShell for shape. */
  uploadedFiles: UploadedFilesStore;
  onUploadedFilesChange: (
    updater: UploadedFilesStore | ((prev: UploadedFilesStore) => UploadedFilesStore),
  ) => void;
  /** Client's chosen PDF language — separate from the advisor's UI locale.
   *  Set by the Welcome step's radio when the workflow has one; drives ONLY
   *  which S3 template gets pulled + filled at PdfGeneration + e-sign. */
  pdfLanguage: 'en' | 'fr';
  /** Welcome step's language radio — updates FormShell's `pdfLanguage`
   *  state (does NOT touch the advisor's UI locale). */
  onPdfLanguageChange: (pdfLanguage: 'en' | 'fr') => void;
  /** Welcome step's CONTINUE — same semantics as the nav row's Next. */
  onContinue: () => void;
  /** The Welcome card's CANCEL — leaves the flow without saving. The advisor page sends it to
   *  BMO's SmartForms landing page (INFLOWS-486 #1). */
  onCancelExit?: () => void;
  /** The ThankYou card's "Back to SmartForm Dashboard" — the advisor's dashboard. Its own exit,
   *  NOT the Welcome cancel's: sharing one sent it to BMO's landing page, which QA's environment
   *  cannot open (Reg/Non-Reg QA D-35 — "Access Denied"). */
  onDashboardExit?: () => void;
}) {
  if (step.type === 'Welcome') {
    // `language` is the advisor's UI locale (from the AppShell header
    // switcher) — drives the Welcome card's own copy (title / description /
    // bullets). `pdfLanguage` is the client's chosen PDF language — the
    // radio binds to it and the callback ONLY updates that, so picking
    // French no longer flips the whole shell.
    return (
      <WelcomeStep
        template={template}
        step={step}
        language={language}
        pdfLanguage={pdfLanguage}
        // Withheld when locked: WelcomeStep disables the radio and hides CONTINUE when it has
        // no handler for them, the same way it already hides CANCEL.
        onPdfLanguageChange={readOnly ? undefined : onPdfLanguageChange}
        onContinue={readOnly ? undefined : onContinue}
        onCancel={onCancelExit}
      />
    );
  }
  if (step.type === 'DynamicForm') {
    return (
      <FormRenderer
        step={step}
        values={values}
        // `readOnly` disables the controls (the affordance); the dead onChange is the guarantee
        // — the address-autocomplete branch and the inline-reveal slots have custom write paths
        // that a `disabled` attribute alone doesn't close. Withholding `mutateValues` also hides
        // a repeatable section's Add / Remove buttons, which are already gated on its presence.
        onChange={readOnly ? noopFieldChange : onChange}
        language={language}
        mode={mode}
        audience={audience}
        readOnly={readOnly}
        addressAutocomplete={addressAutocomplete}
        findUniquenessConflictMessage={findUniquenessConflictMessage}
        mutateValues={readOnly ? undefined : mutateValues}
        allSections={allSectionsOf(template)}
      />
    );
  }
  if (step.type === 'ApplicationReview') {
    // Threads the advisor's uploaded-doc store so the review page recaps
    // "Uploaded documents" + "Generated forms" alongside the entered-data
    // collapse (INFLOWS-201 AC2).
    return (
      <ApplicationReviewStep
        template={template}
        step={step}
        values={values}
        language={language}
        pdfLanguage={pdfLanguage}
        uploadedFiles={uploadedFiles}
        onJumpToStep={onJumpToStep}
        audience={audience}
      />
    );
  }
  if (step.type === 'AdditionalDocuments') {
    return (
      <AdditionalDocumentsStep
        step={step}
        template={template}
        language={language}
        values={values}
        uploadedFiles={uploadedFiles}
        onUploadedFilesChange={readOnly ? noopUploadedFilesChange : onUploadedFilesChange}
        // No uploader on a locked application, for the same reason preview has none: with no
        // handler there is no path into the customer-PII bucket at all.
        uploader={readOnly ? undefined : additionalDocumentUploader}
        // Blocks Next while a file is still uploading, still being scanned, or was refused
        // (ADR-0024, R-24). Second consumer of this prop after OneSpanESignStep. Inert in preview,
        // where files carry neither an upload state nor a scan state.
        onSetStepBlocker={onSetStepBlocker}
      />
    );
  }
  if (step.type === 'ThankYou') {
    // Terminal confirmation. Reports what the Submit button on the previous step already
    // did; it makes no call of its own. See ThankYouStep.
    return (
      <ThankYouStep
        template={template}
        // Carries the authored product name for the headline; absent on a step that was
        // never configured, which falls back to the workflow title.
        step={step}
        language={language}
        // Preview has no advisor dashboard to return to, so the button is withheld rather
        // than pointed at a route the originator cannot use. Same reasoning as the
        // uploader's absence in preview.
        // Reads `audience`, not `mode`: by the time it reaches this component `mode` has
        // been narrowed by rendererModeFor, so public arrives as 'advisor' and cannot be
        // told apart here. The copy is the one thing on this step that genuinely must
        // distinguish them.
        variant={audience === 'public' ? 'public' : 'advisor'}
        // `mode === 'advisor'` still works for the button, and for a different reason:
        // preview passes onSaveAndExit (arriving here as onDashboardExit) and relies on this
        // check to suppress the dashboard link, while public reaches the same `undefined`
        // by absence because its host page passes no onSaveAndExit at all.
        onBackToDashboard={mode === 'advisor' ? onDashboardExit : undefined}
      />
    );
  }
  if (step.type === 'PdfGeneration') {
    // Lists every populated PDF that would be generated for the advisor,
    // plus every advisor-uploaded document threaded through from any
    // AdditionalDocuments step earlier in the flow. No step-blocker
    // wiring here: FormShell hard-swaps Next for a Download button on
    // this step type (see the nav row below), so any blocker published
    // from inside PdfGenerationStep would be inert. If a future flow
    // ever places a step AFTER PdfGeneration with a real Next, revisit
    // this — a generation-in-flight gate would want to live at that
    // downstream Next, not on the PdfGeneration step itself.
    return (
      <PdfGenerationStep
        template={template}
        // PDF template pick uses the client's chosen language (from the
        // Welcome step's radio), NOT the advisor's UI locale.
        language={pdfLanguage}
        values={values}
        cache={pdfCache}
        uploadedFiles={uploadedFiles}
        generatePdf={generatePdf}
        audience={audience}
        pdfTemplateId={pdfTemplateId}
      />
    );
  }
  if (step.type === 'OneSpanESign') {
    // Resolved SIGNATORY payload preview — signers + per-PDF placements.
    // Runtime mode (advisor) also fires ZeroBounce email verification and
    // blocks Next on Invalid signatory emails (INFLOWS-188).
    return (
      <OneSpanESignStep
        template={template}
        step={step}
        values={values}
        language={language}
        mode={mode}
        onSetStepBlocker={onSetStepBlocker}
        // Withheld when locked — the signing-mode Select already renders
        // `disabled={!onChange}`, so this needs no change inside the step. Unread while
        // that column is commented out, but still the step's read-only signal.
        onChange={readOnly ? undefined : onChange}
      />
    );
  }
  // PdfFileUpload (filtered from advisor rail upstream) — placeholder card.
  return <PlaceholderStep step={step} language={language} />;
}

function StepRail({
  steps,
  activeStepId,
  language,
  stepCompletion,
  onSelect,
  clickable,
  lockedStepIds,
}: {
  /** Already filtered to the steps this runtime will walk — audience-eligible AND with
   *  their conditions met. A step the flow skips is absent, not greyed (2026-10-09). */
  steps: WorkflowStep[];
  activeStepId: string;
  /** Advisor only: steps not yet reached. Inert, but NOT greyed — "not there
   *  yet" is a sequencing gate, not a statement about the step. Empty in
   *  preview mode. */
  lockedStepIds: Set<string>;
  language: 'en' | 'fr';
  /** Per-step "should the blue check show" map — pre-computed by FormShell
   *  with the visible / advanced / isStepComplete gates already applied so
   *  this component doesn't re-walk the field tree on every keystroke. */
  stepCompletion: Map<string, boolean>;
  onSelect: (stepId: string) => void;
  clickable: boolean;
}) {
  return (
    <nav
      style={{
        width: 280,
        flexShrink: 0,
        // Plain white surface + 1px right divider. Keeps the rail quiet so
        // the active-step highlight + check icons are the only visual cues.
        // Top padding matches the main pane so the first rail item lines
        // up with the section heading on the canvas — tightened from 32
        // to 20 so the form opens with less wasted vertical real estate.
        //
        // The 32px foot is the drawer's breathing room. On a narrow shell the
        // panel now ends exactly at the card's bottom edge (see
        // `.form-shell__rail` in global.css), so at 16px the last step sat
        // right on the border. It lives on the NAV rather than on the panel
        // deliberately: the panel is the scroll container, and padding on the
        // nav is content — always inside the scrollable area, in every engine —
        // while padding on the panel would also stop the nav's right divider
        // short of the bottom. Invisible on a wide shell, where the column is
        // as tall as the card and nothing reaches the foot.
        background: '#fff',
        borderRight: '1px solid #e5e7eb',
        padding: '20px 0 32px 0',
        position: 'relative',
      }}
    >
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {steps.map((step) => (
          <StepItem
            key={step.id}
            step={step}
            isActive={step.id === activeStepId}
            isComplete={stepCompletion.get(step.id) ?? false}
            language={language}
            clickable={clickable && !lockedStepIds.has(step.id)}
            onSelect={onSelect}
          />
        ))}
      </ul>
    </nav>
  );
}

/**
 * A single rail entry. Four visual states, compounded from isComplete +
 * isActive:
 *
 *   - Completed + not active: BMO-blue check icon + blue text.
 *   - Completed + active: BMO-blue check icon + BLACK bold text (the originator
 *     opened a completed step to inspect it).
 *   - Not completed + not active: grey text, NO icon (icon column reserves
 *     width but is empty so titles still align).
 *   - Not completed + active: grey-not-shown icon + black bold text.
 *
 * Check icon visibility is driven purely by isComplete — never appears on a
 * not-yet-filled step. The icon color is therefore always BMO blue when shown.
 *
 * Wrapped in `React.memo` so a keystroke on the active step's form doesn't
 * re-render every rail row — each StepItem's inputs are scalar primitives +
 * a stable onSelect handler, so the default shallow compare is sufficient.
 */
const StepItem = memo(function StepItem({
  step,
  isActive,
  isComplete,
  language,
  clickable,
  onSelect,
}: {
  step: WorkflowStep;
  isActive: boolean;
  isComplete: boolean;
  language: 'en' | 'fr';
  clickable: boolean;
  onSelect: (stepId: string) => void;
}) {
  // The rail is one of the two surfaces the nav title exists for — the step's own page heading
  // keeps using `titleEn`/`titleFr`. See `stepNavTitle`, which owns the fallback.
  const title = stepNavTitle(step, language);
  // Every row reaching here is a step the flow WILL walk — the caller filters
  // condition-hidden and audience-hidden steps out of the rail's step list in
  // all four runtimes — so there is no "does not apply" visual state to paint.
  // A row can still be un-clickable in the advisor runtime (not reached yet),
  // which is a sequencing gate and deliberately NOT greyed.
  //
  // Per the client's 2026-07-24 Figma the rail is typographic only — state is
  // carried by weight + colour + the check icon, with no background tint and
  // no accent bar:
  //   • complete          → BMO blue + filled check icon in the left cell
  //   • active            → near-black, bold, no icon
  //   • complete + active → check icon stays, but active wins the colour so
  //                         the reader can still find where they are
  //   • neither           → muted grey
  const textColor = isActive ? '#111827' : isComplete ? '#0179C1' : '#475569';
  return (
    <li style={{ listStyle: 'none', position: 'relative' }}>
      <button
        type="button"
        onClick={() => onSelect(step.id)}
        disabled={!clickable}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          // 15px left inset preserves the title alignment the old 3px accent
          // bar + 12px padding produced, so removing the bar doesn't shift
          // every row sideways.
          padding: '8px 16px 8px 15px',
          cursor: clickable ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          position: 'relative',
          zIndex: 1,
          transition: 'color 140ms ease',
        }}
      >
        {/* Icon cell — fully transparent so non-complete rows don't read as
            empty checkboxes. The check icon (only present when complete) is
            the lone visual marker; the cell reserves width so step titles
            still align across rows. */}
        <span
          style={{
            width: 18,
            height: 18,
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isComplete && <CheckCircleFilled style={{ color: '#0179C1', fontSize: 16 }} />}
        </span>
        {/* Clamped to three lines by `.form-shell__rail-title` (global.css), with the
            whole of it on the row's tooltip. A step title is authored text of any
            length, and one long one used to fill the rail top to bottom — on a narrow
            shell that pushed every step after it clean out of the drawer. */}
        <Text
          className="form-shell__rail-title"
          title={title}
          strong={isActive}
          style={{
            fontSize: 14,
            color: textColor,
            letterSpacing: 0.1,
          }}
        >
          {title}
        </Text>
      </button>
    </li>
  );
});

/**
 * "Completed" = every required field on the step has a filled value. Layout
 * blocks (StaticText / Paragraph / Divider) are skipped — they're non-input.
 * A step with zero required fields is vacuously complete from the start.
 *
 * Non-DynamicForm step types (PdfFileUpload / AdditionalDocuments /
 * OneSpanESign / PdfGeneration) report false until their own runtime
 * completion semantics land — otherwise an empty-sections step would always
 * read as "done" the moment the advisor opens it.
 */
/** Steps the advisor reads or confirms but fills nothing in on. */
const PASSIVE_STEP_TYPES: ReadonlySet<WorkflowStep['type']> = new Set([
  'ApplicationReview',
  'OneSpanESign',
  'PdfGeneration',
]);

function isStepComplete(
  step: WorkflowStep,
  values: Record<string, FieldValue>,
  uniquenessOffenders: Set<string>,
  uploadedFiles: UploadedFilesStore,
  /** See missingRequiredFields — same exemption rule, same optionality. */
  sessionUser?: MeResponse | null,
  /** Every section of the workflow, so a condition inside a "repeat once per instance of"
   *  section resolves in its family (see `scopeValuesToInstance`). Optional for tests. */
  allSections?: readonly FormSection[],
  /** See missingRequiredFields — same set, same reason. */
  audience: StepAudience = 'internal',
  /** See missingRequiredFields — same store, same rule. */
  emailLookup?: EmailVerifyLookup,
): boolean {
  const isRequiredExempt = requiredExemptionFor(sessionUser);
  // Fields this audience never sees cannot hold the step back. Identical call to the
  // renderer's, so the two cannot disagree about which field is on screen.
  const withheld = withheldFieldIds(step, audience);
  // Welcome has no inputs — it's complete by construction; the rail check
  // still waits for `advancedSteps` (i.e. CONTINUE pressed) upstream.
  if (step.type === 'Welcome') return true;
  // AdditionalDocuments is complete once every REQUIRED entry has at least
  // one file in the store. The optional bucket doesn't gate completion —
  // it's optional by definition. INFLOWS-201 AC1: "documents ... complete"
  // is enforced here so a required-doc entry left empty blocks Next (via
  // `missingRequiredFields` below) and dims the rail check for this step.
  if (step.type === 'AdditionalDocuments') {
    for (const entry of step.additionalDocumentEntries ?? []) {
      if (!entry.isRequired) continue;
      // Entry-level visibility conditions — mirror the DynamicForm section
      // pattern below. An entry hidden by unmet conditions doesn't count
      // toward completion (otherwise Next would stall on a "required" doc
      // the advisor can't even see).
      if (
        (entry.conditions?.length ?? 0) > 0 &&
        !evaluateConditions(entry.conditions, entry.conditionCombinator, values)
      ) {
        continue;
      }
      // A required entry needs at least one file that could actually be delivered. A file the
      // malware scan refused is not one — without this the rail would paint a blue check on a step
      // holding an infected document, while the Next button beside it stayed disabled.
      //
      // `scanState` is undefined in PREVIEW and for a file still in local state, and
      // `isScanBlocked` treats undefined as fine, so neither is affected.
      const usable = (uploadedFiles.entries[entry.id] ?? []).filter((f) => !isScanBlocked(f));
      if (usable.length === 0) return false;
    }
    return true;
  }
  if (step.type !== 'DynamicForm') return false;
  const hiddenBorrowed = hiddenBorrowedFieldIds(step, values);
  // A hidden borrowed field is no answer (QA D-17: an unticked cheque's amount stayed the funds
  // total's target and blocked a transfer-only application).
  const getValue = (id: string): FieldValue => (hiddenBorrowed.has(id) ? undefined : values[id]);
  // "Disable if" at step level — nothing on the step can be changed, so nothing on it can
  // hold Next back. Same exemption `field.readOnly` already gets via requiredExemptionFor,
  // just driven by a rule instead of a flag.
  const stepIsDisabled = isDisabledByRule(step, values);
  for (const section of step.sections) {
    // A section gated by visibility conditions: only skip it when the rule
    // currently HIDES the section. If the rule matches and the section is
    // showing, its required fields still count toward step completion —
    // otherwise the rail's blue check fires while visible-required-empty
    // fields wait for input.
    if (
      (section.conditions?.length ?? 0) > 0 &&
      !evaluateConditions(section.conditions, section.conditionCombinator, values)
    ) {
      continue;
    }
    const sectionIsDisabled = stepIsDisabled || isDisabledByRule(section, values);
    // §1 Repeatable Sections: every instance's values live under
    // `inst:<sectionId>:<idx>:<fieldId>` keys. Walk the section field-by-
    // field and, for each authored field, check EVERY instance's slot —
    // otherwise a repeatable section's required fields read as empty and
    // Next never enables. Also handles the RepeatableRow-inside-repeatable
    // corner: the row-array itself lives at a qualified key.
    const instanceCount = section.repeatable
      ? getInstanceCount(values, section.id, sectionMinInstances(section))
      : 1;
    for (const row of section.rows) {
      for (const col of row.columns) {
        const f = col.field;
        if (!f) continue;
        if (isLayoutBlockType(f.type)) continue;
        // Borrowed by a CheckboxGroup option that isn't revealing it — the
        // field renders nowhere, so it can't gate completion. Mirrors the
        // same skip in missingRequiredFields.
        if (hiddenBorrowed.has(f.id)) continue;
        // Withheld from this audience — same rule, same reason: it renders nowhere.
        if (withheld.has(f.id)) continue;
        for (let i = 0; i < instanceCount; i++) {
          const key = section.repeatable ? `inst:${section.id}:${i}:${f.id}` : f.id;
          // Field-level conditions: same logic — only skip when the field is
          // currently hidden. Evaluated PER INSTANCE, because a reveal
          // inside a repeatable section is answered separately by each
          // instance: beneficiary 1 may have opened the custodial-parent
          // name fields while beneficiary 2 left them closed, and only
          // the open ones can hold back Next.
          if (
            (f.conditions?.length ?? 0) > 0 &&
            !evaluateConditions(
              f.conditions,
              f.conditionCombinator,
              scopeValuesToInstance(section, values, i, allSections),
            )
          ) {
            continue;
          }
          // Disabled by rule — skip the WHOLE per-instance block, not just the
          // required check. A value the advisor cannot change must not hold Next back
          // for any reason: not required-empty, not a format rule, not a date rule, not
          // a repeatable sum. Per-instance, matching the field-conditions skip above.
          if (
            sectionIsDisabled ||
            isDisabledByRule(f, scopeValuesToInstance(section, values, i, allSections))
          ) {
            continue;
          }
          if (f.required && !isRequiredExempt(f) && !isFilled(values[key])) return false;
          if (failsFormatRule(f, values[key], emailLookup)) return false;
          // Cardinality floor on a CheckboxGroup — "at least N ticked", which
          // `required` (non-empty) can't express.
          if (!isRequiredExempt(f) && !meetsMinSelections(f.minSelections, values[key])) {
            return false;
          }
          if (failsDateRule(f, values[key], section, values, i, allSections)) return false;
          if (failsNumberRange(f, values[key])) return false;
          if (f.type === 'RepeatableRow') {
            const rows = (values[key] as RepeatableRowValue[] | undefined) ?? [];
            if (f.sumValidation && !isSumSatisfied(f, rows, getValue)) return false;
            if (incompleteRepeatableColumns(f, rows, 'en', emailLookup).length > 0) return false;
          }
        }
        // Uniqueness offender set is precomputed across the whole workflow
        // (visibility-aware) — a duplicate ANYWHERE blocks the offending
        // step's completion. Keyed on the bare authored field id today;
        // when uniqueness gains per-instance semantics the offender set
        // will hold qualified keys and this check will follow.
        if (hasUniquenessGroup(f) && uniquenessOffenders.has(f.id)) return false;
      }
    }
  }
  return true;
}

/**
 * Walk the active step's visible fields and return the first one whose
 * current value matches its `blockingValues` gate (see isBlockingValueMatch).
 * That's the "dead-end answer" gate the originator authored — an explicit
 * eligibility short-circuit that disables Next regardless of how many other
 * fields are filled. Returns null when nothing's blocking.
 */
function findBlockingField(
  step: WorkflowStep,
  values: Record<string, FieldValue>,
): WorkflowField | null {
  // Only DynamicForm steps own input fields with blocking values.
  if (step.type !== 'DynamicForm') return null;
  for (const section of step.sections) {
    if (
      (section.conditions?.length ?? 0) > 0 &&
      !evaluateConditions(section.conditions, section.conditionCombinator, values)
    ) {
      continue;
    }
    for (const row of section.rows) {
      for (const col of row.columns) {
        const f = col.field;
        if (!f || !f.blockingValues || f.blockingValues.length === 0) continue;
        if (
          (f.conditions?.length ?? 0) > 0 &&
          !evaluateConditions(f.conditions, f.conditionCombinator, values)
        ) {
          continue;
        }
        if (isBlockingValueMatch(f, values[f.id])) return f;
      }
    }
  }
  return null;
}

/**
 * Required COLUMNS inside a RepeatableRow that the advisor has left empty — and filled cells
 * that break one of the column's rules (see `columnCellViolation`).
 *
 * The completeness walk only ever checked the field-level `required` (is the
 * row array non-empty) and `sumValidation` — a column marked required inside
 * the row template was never enforced, so a half-filled owner or trustee row
 * sailed past Next.
 *
 * Only rows the advisor has STARTED are checked: an untouched RepeatableRow
 * renders one blank placeholder row, and demanding its columns would block an
 * optional list nobody intended to fill. A field that must have at least one
 * entry expresses that with its own `required` flag, which is checked
 * separately.
 */
function incompleteRepeatableColumns(
  field: WorkflowField,
  rows: RepeatableRowValue[],
  language: 'en' | 'fr',
  /** See missingRequiredFields — an `email` column is gated on its ZeroBounce verdict too. */
  emailLookup?: EmailVerifyLookup,
  /** Answer map, so a dual-unit column's `{type}` token names the unit the advisor is
   *  actually in. Optional: without it the token falls back to the authored default,
   *  which is what the boolean-only `isStepComplete` caller wants. */
  getValue?: (id: string) => FieldValue,
): FieldIssue[] {
  const template = field.repeatableTemplate ?? [];
  if (template.length === 0) return [];
  const out: FieldIssue[] = [];
  for (const [rowIdx, row] of rows.entries()) {
    // `checkBlankRows` (opt-in, QA TFSA D08): a row the advisor ADDED counts even left blank — they
    // asked for it, so Next names its cells instead of quietly dropping it. Row 1 stays governed by
    // the field's own `required`, as before.
    const started =
      template.some((c) => isFilled(row[c.id] as FieldValue)) ||
      (!!field.checkBlankRows && rowIdx > 0);
    if (!started) continue;
    for (const col of template) {
      const cell = row[col.id] as FieldValue;
      // A required cell left blank, or a filled cell breaking one of the column's own rules — the
      // wrong length (the five-digit "Fund number"), a failed regex ("Wire order number"), a past
      // date on a no-past column ("Start date"). A blank optional cell is fine either way.
      const bad = isFilled(cell)
        ? columnCellViolation(col, cell, undefined, emailLookup) !== null
        : col.required;
      if (!bad) continue;
      const label = resolveColumnLabel(
        col,
        language,
        col.unitToggle ? readUnitMode(field, col, getValue) : null,
      );
      // A blank cell is nothing to correct and a filled one is nothing to fill — the two
      // halves of `bad` above are exactly the two kinds, so the split costs nothing here.
      out.push({ label, kind: isFilled(cell) ? 'invalid' : 'missing' });
    }
  }
  return out;
}

/**
 * Why a field is holding Next back.
 *
 * `'missing'` is an empty box, `'invalid'` is a box with the wrong thing in it. The
 * distinction exists for one reason: the summary toast. Telling an advisor to "fill" a
 * Subscription amount they can see their own 1000 sitting in — because it is under the
 * series minimum — reads as the application having lost their typing, and sends them back
 * to a field that is already filled rather than to the red text underneath it.
 *
 * Nothing else branches on it. Both kinds block Next identically, and the per-field red
 * text was always the one saying what is actually wrong.
 */
export type FieldIssueKind = 'missing' | 'invalid';

export type FieldIssue = {
  /** The field's label, as it is named in the toast and the Next tooltip. */
  label: string;
  kind: FieldIssueKind;
};

/**
 * The summary line for a held Next — "Please fill: A, B", "Please correct: C", or the
 * either/or wording when the step has some of each.
 *
 * Collapses to a generic sentence past three names, exactly as it always did, so the
 * toast and the tooltip stay readable on a long step.
 */
export function nextBlockedMessage(
  issues: readonly FieldIssue[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (issues.length === 0) return '';
  const hasMissing = issues.some((i) => i.kind === 'missing');
  const hasInvalid = issues.some((i) => i.kind === 'invalid');
  const key =
    issues.length <= 3
      ? hasInvalid
        ? hasMissing
          ? 'formRenderer.nextBlockedMixedFields'
          : 'formRenderer.nextBlockedInvalidFields'
        : 'formRenderer.nextBlockedFields'
      : hasInvalid
        ? 'formRenderer.nextBlockedManyProblems'
        : 'formRenderer.nextBlockedManyFields';
  return t(key, { fields: issues.map((i) => i.label).join(', ') });
}

/**
 * The labels alone, for callers that only need to name what is blocking.
 *
 * Kept as the public entry point it has always been — `blockingFieldIssues` is the same
 * walk, and the only reason to reach for it is the toast's fill-vs-correct wording.
 */
export function missingRequiredFields(...args: Parameters<typeof blockingFieldIssues>): string[] {
  return blockingFieldIssues(...args).map((i) => i.label);
}

/**
 * Walk the active step's visible sections + fields and return the bilingual label of every
 * one holding Next back, each tagged with WHY (see {@link FieldIssue}). Same visibility
 * rules as `isStepComplete` — a section / field hidden by current values doesn't count.
 * Returns an array (possibly empty) so the caller can decide whether to name the fields or
 * fall back to a generic sentence.
 */
export function blockingFieldIssues(
  step: WorkflowStep,
  values: Record<string, FieldValue>,
  language: 'en' | 'fr',
  uniquenessOffenders: Set<string>,
  uploadedFiles: UploadedFilesStore,
  /** Signed-in advisor, for the read-only required-exemption rule. Optional so
   *  existing callers (and tests) that don't care keep working — a null session
   *  exempts every read-only field, which is the pre-AC3 behaviour. */
  sessionUser?: MeResponse | null,
  /** See isStepComplete. */
  allSections?: readonly FormSection[],
  /**
   * Who is filling the form in. A PUBLIC customer never sees the advisor-sourced
   * fields, so naming one in the Next tooltip would point at a control that is not
   * on the page — and counting a REQUIRED one would block Next forever, which is
   * exactly the bug this audience rule fixes (an `advisorRepCodeSelector` is forced
   * Required and refused `readOnly`, so the read-only exemption never covers it).
   *
   * Defaults to `'internal'`, which withholds nothing.
   */
  audience: StepAudience = 'internal',
  /**
   * The ZeroBounce store, so an address the service called undeliverable holds Next and gets
   * named in the tooltip — the same treatment a malformed one gets from `failsFormatRule`,
   * and for the same reason it was given one (QA D-32: red text that didn't hold Next let an
   * invalid value through to the review and the PDFs).
   *
   * Omitted — by a caller that has no store, and by the public runtime, where the capability
   * is off — every email verdict reads as "nothing to say" and the gate behaves exactly as it
   * did before verification existed.
   */
  emailLookup?: EmailVerifyLookup,
): FieldIssue[] {
  const isRequiredExempt = requiredExemptionFor(sessionUser);
  const withheld = withheldFieldIds(step, audience);
  // AdditionalDocuments: report every required entry that has no uploaded
  // file, using the entry's label as the tooltip token. Optional bucket is
  // ignored (optional by definition).
  if (step.type === 'AdditionalDocuments') {
    const out: FieldIssue[] = [];
    for (const entry of step.additionalDocumentEntries ?? []) {
      if (!entry.isRequired) continue;
      // See the matching branch in `isStepComplete` — conditional entries
      // that are currently hidden don't participate in the missing-fields
      // count.
      if (
        (entry.conditions?.length ?? 0) > 0 &&
        !evaluateConditions(entry.conditions, entry.conditionCombinator, values)
      ) {
        continue;
      }
      if ((uploadedFiles.entries[entry.id] ?? []).length > 0) continue;
      // Nothing uploaded is nothing to correct: a document entry only ever goes missing.
      out.push({ label: localizedEntryLabel(entry, language), kind: 'missing' });
    }
    return out;
  }
  // Only DynamicForm steps have field-level required-input semantics.
  if (step.type !== 'DynamicForm') return [];
  const out: FieldIssue[] = [];
  const hiddenBorrowed = hiddenBorrowedFieldIds(step, values);
  // A hidden borrowed field is no answer (QA D-17: an unticked cheque's amount stayed the funds
  // total's target and blocked a transfer-only application).
  const getValue = (id: string): FieldValue => (hiddenBorrowed.has(id) ? undefined : values[id]);
  const seen = new Set<string>();
  // First kind wins for a given label, and the order below is deliberate: `required`-empty is
  // checked before every rule, so a field that is both empty and (vacuously) rule-breaking is
  // reported as the blank it is. A label shared by two different fields still collapses to one
  // entry, exactly as it did when this list was plain strings.
  const pushOnce = (msg: string, kind: FieldIssueKind): void => {
    if (seen.has(msg)) return;
    seen.add(msg);
    out.push({ label: msg, kind });
  };
  // Kept in lockstep with `isStepComplete` — see the matching block there.
  const stepIsDisabled = isDisabledByRule(step, values);
  for (const section of step.sections) {
    if (
      (section.conditions?.length ?? 0) > 0 &&
      !evaluateConditions(section.conditions, section.conditionCombinator, values)
    ) {
      continue;
    }
    const sectionIsDisabled = stepIsDisabled || isDisabledByRule(section, values);
    // §1 Repeatable Sections: walk every instance's qualified key when
    // the section is repeatable. Otherwise fall back to the bare field
    // id (one iteration). Deduping via `seen` keeps the tooltip terse
    // even when the same field is empty across multiple instances.
    const instanceCount = section.repeatable
      ? getInstanceCount(values, section.id, sectionMinInstances(section))
      : 1;
    const instanceLabel = (n: number): string | null =>
      section.repeatable ? ` (${renderInstanceLabel(section, n, language)})` : null;
    for (const row of section.rows) {
      for (const col of row.columns) {
        const f = col.field;
        if (!f) continue;
        if (isLayoutBlockType(f.type)) continue;
        // Borrowed by an option that isn't revealing it — invisible, so not
        // required. See hiddenBorrowedFieldIds.
        if (hiddenBorrowed.has(f.id)) continue;
        // Withheld from this audience — invisible for a different reason, same effect.
        if (withheld.has(f.id)) continue;
        // Non-repeatable fast path. Inside a repeatable section the same
        // check runs per instance below, because each instance answers
        // the reveal for itself.
        if (
          !section.repeatable &&
          (f.conditions?.length ?? 0) > 0 &&
          !evaluateConditions(f.conditions, f.conditionCombinator, values)
        ) {
          continue;
        }
        const label = (language === 'fr' && f.labelFr ? f.labelFr : f.labelEn) || f.labelEn;
        const cleaned = label.replace(/\*\*/g, '').trim();
        let requiredHit = false;
        for (let i = 0; i < instanceCount; i++) {
          const key = section.repeatable ? `inst:${section.id}:${i}:${f.id}` : f.id;
          if (
            section.repeatable &&
            (f.conditions?.length ?? 0) > 0 &&
            !evaluateConditions(
              f.conditions,
              f.conditionCombinator,
              scopeValuesToInstance(section, values, i, allSections),
            )
          ) {
            continue;
          }
          // Disabled by rule — never named in the Next tooltip, for the same reason it
          // never blocks completion: the advisor has no way to act on it. Skips the whole
          // per-instance block, matching `isStepComplete`.
          if (
            sectionIsDisabled ||
            isDisabledByRule(f, scopeValuesToInstance(section, values, i, allSections))
          ) {
            continue;
          }
          if (f.required && !isRequiredExempt(f) && !isFilled(values[key])) {
            // Repeat-section: name the instance so the advisor knows
            // which one is short a required field.
            const suffix = instanceLabel(i);
            pushOnce(suffix ? `${cleaned}${suffix}` : cleaned, 'missing');
            requiredHit = true;
          }
          // Filled but malformed — the SIN / email / phone / postal / regex rule the field
          // shows in red on blur. Without this the red text was advisory only and Next
          // carried an invalid SIN into the review and the PDFs (QA D-32).
          if (failsFormatRule(f, values[key], emailLookup)) {
            const suffix = instanceLabel(i);
            pushOnce(suffix ? `${cleaned}${suffix}` : cleaned, 'invalid');
            requiredHit = true;
          }
          // A Date breaking one of its own rules (min-age / no-future, no-past,
          // must-follow) blocks Next like an empty required field. The picker
          // only greys the calendar; typed input — and a must-follow date whose
          // reference moved afterwards — reaches here. Checked per instance, so
          // each instance's end date is compared with ITS start date.
          if (failsDateRule(f, values[key], section, values, i, allSections)) {
            const suffix = instanceLabel(i);
            pushOnce(suffix ? `${cleaned}${suffix}` : cleaned, 'invalid');
            requiredHit = true;
          }
          // A Number over its Maximum (or under its Minimum) blocks Next and gets named in
          // the tooltip, like the malformed SIN above — the control caps the field's width,
          // not its value, so an over-maximum figure genuinely reaches here. INVALID rather
          // than missing: the series minimum is the whole reason the two kinds exist — an
          // advisor told to "fill" the Subscription amount they just typed 1000 into looks
          // for a blank box instead of at the red line under it.
          if (failsNumberRange(f, values[key])) {
            const suffix = instanceLabel(i);
            pushOnce(suffix ? `${cleaned}${suffix}` : cleaned, 'invalid');
            requiredHit = true;
          }
          // Enough boxes ticked? Named the same way as a missing required
          // field so the disabled-Next tooltip reads consistently — and it IS
          // a missing one: too few ticks is an absent answer, not a wrong one.
          if (!isRequiredExempt(f) && !meetsMinSelections(f.minSelections, values[key])) {
            const suffix = instanceLabel(i);
            pushOnce(suffix ? `${cleaned}${suffix}` : cleaned, 'missing');
            requiredHit = true;
          }
          if (f.type === 'RepeatableRow') {
            const rows = (values[key] as RepeatableRowValue[] | undefined) ?? [];
            // A dual-unit column whose ACTIVE mode carries a total must
            // balance too. Named per COLUMN, like the incomplete-column check
            // below, so the advisor knows WHICH total is off rather than just
            // that the table is wrong. A total that does not add up is figures
            // to correct, not boxes to fill.
            for (const u of unbalancedUnitColumns(f, rows, getValue)) {
              const colLabel = resolveColumnLabel(u.column, language, u.mode);
              pushOnce(`${cleaned} — ${colLabel}`, 'invalid');
            }
            if (f.sumValidation && !isSumSatisfied(f, rows, getValue)) {
              const suffix = instanceLabel(i);
              pushOnce(suffix ? `${cleaned}${suffix}` : cleaned, 'invalid');
            }
            // Name the COLUMN, prefixed by the list, so "Owners of interest —
            // Category" tells the advisor exactly what to finish. The column
            // walk already knows whether each cell is blank or wrong.
            for (const cell of incompleteRepeatableColumns(
              f,
              rows,
              language,
              emailLookup,
              getValue,
            )) {
              pushOnce(`${cleaned} — ${cell.label}`, cell.kind);
            }
          }
        }
        if (requiredHit) continue;
        // Cross-field uniqueness — surface offenders on this step so the
        // tooltip names the affected input. The per-field inline error
        // still shows via the on-blur check.
        // A duplicate is a filled box holding a value that has to change — invalid.
        if (hasUniquenessGroup(f) && uniquenessOffenders.has(f.id)) pushOnce(cleaned, 'invalid');
      }
    }
  }
  return out;
}

/**
 * True when a field holds a value its own format rule rejects — the same `runValidate` call
 * FieldRenderer makes on blur, so Next and the red error text can never disagree. Blank is not
 * a format failure (`required` owns that). A read-only field is exempt: its value was prefilled
 * and the advisor has no way to correct it.
 *
 * ZeroBounce rides along here rather than in a gate of its own, because to this function's
 * callers it IS a format rule: an email the service says has no mailbox behind it is a value
 * the person has to change before Next, exactly like a SIN that fails its checksum. The only
 * difference is that the answer arrives a few hundred milliseconds late, which is what the
 * `checking` verdict covers — see `emailVerifyGate.ts` for the whole rule and for why an
 * `Unknown` passes here while it hard-blocks on the e-signature step.
 */
function failsFormatRule(
  f: WorkflowField,
  v: FieldValue,
  emailLookup?: EmailVerifyLookup,
): boolean {
  if (f.readOnly || typeof v !== 'string') return false;
  // A display mask's value is complete only with every digit slot filled (QA D-21).
  if (f.type === 'ShortText' && f.displayMask && isMaskIncomplete(f.displayMask, v)) return true;
  // Authored min / max length (see lengthRule.ts — the DOM maxlength alone is bypassable).
  if (fieldLengthViolation(f, v)) return true;
  if (!f.validate) return false;
  if (runValidate(f.validate as ValidateRule, v, f.regexPattern) !== null) return true;
  // Syntax passed. Only now is a verdict meaningful — and only an email field has one.
  return f.validate === 'email' && emailValueVerdict(v, emailLookup) !== null;
}

/**
 * True when a Number field's figure breaks its own authored Minimum / Maximum — the same
 * `fieldNumberRangeViolation` call FieldRenderer makes on blur, so Next and the red error text
 * can never disagree. Lives apart from `failsFormatRule` because that one only ever sees
 * strings; a Number's value reaches here as a number.
 *
 * Needed because the control no longer clamps: it caps how WIDE a figure may be (a maximum of
 * 123 sizes a three-digit box) and lets 800 be typed, so without this the over-maximum figure
 * would carry through to the review and the PDFs the way QA D-32's invalid SIN did.
 */
function failsNumberRange(f: WorkflowField, v: FieldValue): boolean {
  return fieldNumberRangeViolation(f, v) !== null;
}

/**
 * True when a Date field's value breaks one of its own rules — the same `validateDateRules` call
 * FieldRenderer makes on blur, so Next and the red error text can never disagree. A
 * `mustFollowFieldId` reference resolves the way this instance's conditions do
 * (`scopeValuesToInstance`): a start date in the same repeatable instance, else the bare key.
 */
function failsDateRule(
  f: WorkflowField,
  v: FieldValue,
  section: FormSection,
  values: Record<string, FieldValue>,
  instanceIdx: number,
  allSections?: readonly FormSection[],
): boolean {
  if (f.type !== 'Date') return false;
  const mustFollowValue = f.mustFollowFieldId
    ? scopeValuesToInstance(section, values, instanceIdx, allSections)[f.mustFollowFieldId]
    : undefined;
  return validateDateRules(f, typeof v === 'string' ? v : null, { mustFollowValue }) !== null;
}

function isNonEmptyValue(v: FieldValue | undefined): boolean {
  return v !== undefined && v !== null && !(typeof v === 'string' && v.trim() === '');
}

function isFilled(v: FieldValue): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (typeof v === 'number') return Number.isFinite(v);
  // Required Toggle / Checkbox is the acknowledgment pattern — must be ON
  // to count as "filled."
  if (typeof v === 'boolean') return v;
  if (Array.isArray(v)) {
    if (v.length === 0) return false;
    // RepeatableRow value — array of objects keyed by column id. An array
    // of all-empty rows is functionally unfilled (the renderer creates a
    // placeholder row by default, so an untouched RepeatableRow looks like
    // `[{col_a: null, col_b: null}]`). Probe the FIRST non-null element to
    // distinguish RepeatableRow (objects) from CheckboxGroup (strings) —
    // a `[null, ...]` leading-null shape used to slip through the
    // `typeof v[0] === 'object'` check and unconditionally read as filled.
    const probe = (v as unknown[]).find((x) => x !== null && x !== undefined);
    if (typeof probe === 'object' && probe !== null) {
      return (v as Array<Record<string, unknown> | null>).some(
        (row) =>
          row !== null &&
          Object.values(row).some(
            (cell) =>
              cell !== null &&
              cell !== undefined &&
              cell !== '' &&
              // NaN slips through the !== '' check; treat as unfilled so a
              // corrupted numeric cell doesn't paint the rail's blue check.
              (typeof cell !== 'number' || Number.isFinite(cell)),
          ),
      );
    }
    // CheckboxGroup `string[]` — non-empty array is filled.
    return true;
  }
  return false;
}

/**
 * Why a blocking answer stops Next: the originator-authored block-continue explanation (FR falls
 * back to EN), or generic "doesn't qualify" copy naming the field when none is authored.
 */
export function blockingAnswerMessage(
  field: WorkflowField,
  language: 'en' | 'fr',
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const authored =
    (language === 'fr' && field.blockContinueMessageFr?.trim()
      ? field.blockContinueMessageFr
      : field.blockContinueMessageEn) ?? '';
  if (authored.trim()) return authored.trim();
  const label =
    (language === 'fr' && field.labelFr ? field.labelFr : field.labelEn) || field.labelEn;
  return t('formRenderer.nextBlockedByAnswer', { field: label.replace(/\*\*/g, '').trim() });
}
